// Ultimate Card Game (UCG) - Battlefield & Lane Combat Simulation

import { Unit } from './unit.js';
import { CARDS_DATA, getCardByName } from '../data/cards.js';
import { UnseededRNG } from './rng.js';
import { BALANCE } from '../data/balance.js';
import { MatchStats } from './stats.js';
import { ModuleSystem } from './modules.js';
import { getSwampFogIntensity } from './maps.js';

export class Battlefield {
  // Ground turntable mount offset of the base's KUKA-arm turret on the
  // bunker foundation (scaled +8% with BASE_SCALE 0.864: 76 * 1.08 = 82, 9 * 1.08 = 10).
  static BASE_TURRET_DX = 82;
  static BASE_TURRET_DY = 10;
  // Sul Pyramidal Apex Center offset (firing point of the apex energy sphere, elevated for +5% pyramid scale)
  static SUL_APEX_DX = 50;
  static SUL_APEX_DY = -57;
  // Distance from the map edge to a base's x. Must clear the human fortress's
  // bunker dock-block (88 wide, offset 40 outward from base.x, then scaled by
  // BASE_SCALE*BLDG_SCALE in renderer.js's renderBase()) or its outward edge
  // renders past world x=0/width and gets clipped by the render canvas
  // bounds - verified empirically the block's outward edge needs >=42.
  static BASE_EDGE_X = 46;

  constructor(width = 1600, height = 540, rng = null) {
    if (typeof width === 'object' && width !== null && (typeof width.next === 'function' || typeof width.range === 'function')) {
      rng = width;
      width = 1600;
      height = 540;
    }
    this.width = typeof width === 'number' && Number.isFinite(width) ? width : 1600;
    this.height = typeof height === 'number' && Number.isFinite(height) ? height : 540;
    // Seeded in online matches so both peers spawn units at identical
    // offsets from identical commands; falls back to Math.random() locally.
    this.rng = rng || UnseededRNG;
    this.laneHeight = 320;
    // Fixed sky depth from the top, independent of total canvas height, so
    // trimming the canvas only eats into the foreground band below the lane
    // instead of squeezing the sky and lane symmetrically.
    this.laneY = 176;

    this._economy = null; // Set by Game after construction
    this.stats = new MatchStats();
    // Cosmetic only (see js/engine/maps.js) - purely a rendering choice for
    // background.js, never read by the simulation itself. Defaults to the
    // original map so headless sims/tests that construct Battlefield
    // directly (bypassing main.js's start*Match()) don't need to care.
    this.mapId = 'human';
    this.mapSeed = 0;

    this.initBases();

    // Base modules (stacked base extensions - see js/engine/modules.js).
    // Owned by the Battlefield rather than by Game, unlike the legacy
    // BuildingSystem, so it ticks inside update() on every client and in
    // headless tests instead of depending on main.js's render loop.
    this.moduleSystem = new ModuleSystem(this);

    // Active units in lane
    this.units = [];
    this.structures = [];
    this.goldenStatues = [];
    this.cocoons = [];
    this.metamorphosisFogTimer = 0;
    this.metamorphosisFogTeam = null;
    // O(1) id -> Unit lookup (tether targets, execution targets) kept in
    // sync with this.units in spawnRaw()/reset() and the death-removal loop
    // in update(), instead of a linear .find() per lookup (see
    // docs/260906_performanceimplementation_plan.md Pillar 4).
    this.unitMap = new Map();
    // Compact list of active detector units per team for O(1) stealth detection checks
    this.detectors = { western: [], eastern: [] };
    // Pre-allocated collision spatial partitioning buckets (Zero-Allocation & Invariante #9)
    this._groundBuckets = Array.from({ length: 20 }, () => []);
    this._airBuckets = Array.from({ length: 20 }, () => []);
    this.tickCount = 0;
    // Deterministic in-flight simulation queues (decoupled from particles rendering)
    this.pendingProjectiles = [];
    this.pendingCapsules = [];
    this.pendingDrones = [];
    this.deployBeacons = [];
    // Audio & Particle Event buffer for Host-State Streaming
    this.frameEvents = [];
    // Per-match, not module-global: two peers who both start a match at
    // "reset()" assign identical ids to identically-spawned units, so a
    // command could reference a unit by id and resolve to the same unit on
    // both sides. Covers units spawned directly by CardSystem/Battlefield
    // as well as units units spawn on their own (mines, broodlings, bunker
    // crew, clones) via nextUnitId() below.
    this.unitIdCounter = 0;
    this.winner = null;
    this.isGameOver = false;

    // Nuclear & Environmental States
    this.isNuked = false;
    this.nukeTimer = 0;
    this.radioactiveCraters = [];
    this.scarType = null; // 'nuclear' | 'bioplasma'

    // Territorial Dominance & Midline Control
    this.territoryControl = 'neutral'; // 'western', 'eastern', or 'neutral'
    this.territoryDivider = width / 2; // Smooth X position of territory frontline (default 800)
    this.territoryBonus = 0; // current plasma bonus fraction, see updateTerritorialControl()
    this.territoryStillstandTimer = 0; // standstill timer before decaying to center
    this.isTerritoryLocked = false; // true while locked by Claiming Pillar
    this.territoryLockedTeam = null; // team that controls the dominant locking pillar

    // Active Sul Precision Strikes & Retreat Protocol Signals
    this.activePrecisionStrikes = [];
    this.activeRetreatSignals = [];
  }

  // Frontline mechanics: the frontline moves when a unit is actually standing
  // beyond it. When no unit is pushing the indicator (standstill), after
  // BALANCE.territoryStillstandDelay (3 seconds), the indicator slowly
  // returns toward the midline (center), unless units push it again.
  // The plasma bonus it grants scales quadratically with how deep into enemy
  // territory it sits (0% at the midline, BALANCE.territoryMaxBonus right
  // at the enemy base).
  updateTerritorialControl(dt) {
    const midX = this.width / 2; // 800
    const line = this.territoryDivider;

    // Check for living Claiming Pillar structures
    let activeClaimingPillar = null;
    let westHasClaimingPillar = false;
    let eastHasClaimingPillar = false;
    if (this.units) {
      for (let i = 0; i < this.units.length; i++) {
        const u = this.units[i];
        if (u.isClaimingPillar && !u.isDead) {
          if (!activeClaimingPillar) activeClaimingPillar = u; // First pillar dominates!
          if (u.team === 'western') westHasClaimingPillar = true;
          if (u.team === 'eastern') eastHasClaimingPillar = true;
        }
      }
    }

    if (activeClaimingPillar) {
      // Locked Frontline: Claiming Pillar freezes the indicator at its position
      this.territoryDivider = activeClaimingPillar.x;
      this.territoryStillstandTimer = 0;
      this.isTerritoryLocked = true;
      this.territoryLockedTeam = activeClaimingPillar.team;
    } else {
      this.isTerritoryLocked = false;
      this.territoryLockedTeam = null;

      // Single pass instead of two .filter() allocations per frame - only the
      // running max/min actually matter, not the filtered arrays themselves.
      let maxWestX = 0;
      let minEastX = this.width;
      for (const u of this.units) {
        if (u.isDead || u.isStructure || u.isGoldenStatue || u.isCocoon) continue;
        if (u.team === 'western') {
          if (u.x > maxWestX) maxWestX = u.x;
        } else if (u.team === 'eastern') {
          if (u.x < minEastX) minEastX = u.x;
        }
      }

      // A side only ever moves the line by having a unit further past it
      // than the line currently sits - it never "holds" the line by simply
      // existing behind it. If neither side has a unit past the current
      // line, the line enters standstill and decays toward center after 3s.
      const deltaWest = maxWestX > line ? (maxWestX - line) : 0;
      const deltaEast = minEastX < line ? (line - minEastX) : 0;
      const isPushing = deltaWest > 0 || deltaEast > 0;

      if (isPushing) {
        this.territoryStillstandTimer = 0;
        // Net displacement / tug-of-war (Option A):
        // When both sides push past the line simultaneously (e.g. units crossing),
        // their displacements counter each other smoothly rather than binary winner-take-all
        // oscillation. When only one side pushes, targetDivider is simply that unit's position.
        const targetDivider = line + (deltaWest - deltaEast);
        this.territoryDivider += (targetDivider - this.territoryDivider) * BALANCE.territoryLerpSpeed * dt;
      } else {
        // No active unit pushing past the current line
        if (Math.abs(this.territoryDivider - midX) > 0.01) {
          this.territoryStillstandTimer += dt;
          const stillstandDelay = BALANCE.territoryStillstandDelay !== undefined ? BALANCE.territoryStillstandDelay : 3.0;
          if (this.territoryStillstandTimer >= stillstandDelay) {
            const decaySpeed = BALANCE.territoryDecaySpeed !== undefined ? BALANCE.territoryDecaySpeed : 30;
            if (this.territoryDivider > midX) {
              this.territoryDivider = Math.max(midX, this.territoryDivider - decaySpeed * dt);
            } else if (this.territoryDivider < midX) {
              this.territoryDivider = Math.min(midX, this.territoryDivider + decaySpeed * dt);
            }
          }
        } else {
          this.territoryDivider = midX;
          this.territoryStillstandTimer = 0;
        }
      }
    }
    // Base radius is 48px at x=BASE_EDGE_X / x=width-BASE_EDGE_X - never let
    // the line reach inside a base.
    const territoryClampMargin = Battlefield.BASE_EDGE_X + 48;
    this.territoryDivider = Math.max(territoryClampMargin, Math.min(this.width - territoryClampMargin, this.territoryDivider));

    const deadZone = BALANCE.territoryDeadZone !== undefined ? BALANCE.territoryDeadZone : 15;
    this.territoryControl = this.territoryDivider > (midX + deadZone) ? 'western'
      : this.territoryDivider < (midX - deadZone) ? 'eastern'
      : 'neutral';

    // d: 0 at the midline/deadzone, 1 at the far base. bonus: quadratic, so a small
    // advance barely matters but a deep one pays off fast near the end.
    const halfWidth = midX - Battlefield.BASE_EDGE_X; // distance from midline to the near edge of a base
    const effectiveDist = Math.max(0, Math.abs(this.territoryDivider - midX) - deadZone);
    const maxDist = Math.max(1, halfWidth - deadZone);
    const d = this.territoryControl === 'neutral' ? 0 : Math.max(0, Math.min(1, effectiveDist / maxDist));
    const bonus = this.territoryControl === 'neutral' ? 0 : BALANCE.territoryMaxBonus * d * d;

    // Double bonus income ONLY for the team that owns the Claiming Pillar
    const westBonus = (this.territoryControl === 'western') ? (westHasClaimingPillar ? bonus * 2 : bonus) : 0;
    const eastBonus = (this.territoryControl === 'eastern') ? (eastHasClaimingPillar ? bonus * 2 : bonus) : 0;

    if (this.economy) {
      const resW = this.economy.resources.western;
      const resE = this.economy.resources.eastern;
      resW.plasmaMultiplier = 1 + westBonus;
      resE.plasmaMultiplier = 1 + eastBonus;
    }

    this.stats?.recordTerritory(this.territoryDivider, this.width, dt);

    // Exposed for the renderer's territory-bonus label - see
    // js/engine/renderer.js.
    this.territoryBonus = (this.territoryControl === 'western' ? westBonus : (this.territoryControl === 'eastern' ? eastBonus : 0));
  }

  // Returns the visual center for camera framing: primarily tracks the bonus indicator
  // (territoryDivider / frontline beacon) as requested.
  getCameraFocalX() {
    return this.territoryDivider ?? (this.width / 2);
  }

  // Purely read-only camera framing helper (never mutates simulation state).
  // Detects whether an active frontline combat engagement is taking place and
  // returns the X positions of both the western and eastern combat parties so
  // the Portrait camera can smoothly pan toward the defender and frame both sides.
  getCameraFramingInfo(myTeam = 'western') {
    const dividerX = this.territoryDivider ?? (this.width / 2);
    if (!this._cameraFramingResult) {
      this._cameraFramingResult = {
        focalX: dividerX,
        inCombat: false,
        leftCombatX: dividerX,
        rightCombatX: dividerX,
        attackerX: dividerX,
        defenderX: dividerX,
        myTeam: 'western'
      };
    }
    const out = this._cameraFramingResult;
    out.focalX = dividerX;
    out.inCombat = false;
    out.leftCombatX = dividerX;
    out.rightCombatX = dividerX;
    out.attackerX = dividerX;
    out.defenderX = dividerX;
    out.myTeam = myTeam || 'western';

    if (!this.units || this.units.length === 0) return out;

    const getEntityCombatX = (ent) => {
      if (!ent) return dividerX;
      if (ent === this.bases?.western || ent.id === 'base_western') {
        return ent.x + (ent.radius || 38);
      }
      if (ent === this.bases?.eastern || ent.id === 'base_eastern') {
        return ent.x - (ent.radius || 38);
      }
      return ent.x;
    };

    // Find the primary frontline engagement (closest active combat pair to territoryDivider)
    let bestPairDist = Infinity;
    let leadWestX = null;
    let leadEastX = null;

    for (let i = 0; i < this.units.length; i++) {
      const u = this.units[i];
      if (!u || u.isDead || u.isCinematic || u.isGoldenStatue || u.isCocoon || u.isParachuting) continue;
      const t = u.target;
      if (!t || t.isDead || !t.team || t.team === u.team) continue;

      const uX = getEntityCombatX(u);
      const tX = getEntityCombatX(t);
      const dist = Math.abs(t.x - u.x);
      const maxRange = (u.range || 24) + (u.radius || 20) + (t.radius || 20) + 18;
      // An entity is only actively fighting if it is actually within combat range of its target,
      // or actively engaged in close proximity (dist <= maxRange + 36).
      // Crucial: u.attackCooldown > 0 alone without range MUST NEVER count as fighting,
      // because all newly spawned units spawn at their home base with attackCooldown > 0
      // while their default target is the enemy base 1400px away.
      const inRange = this.isInRange ? this.isInRange(u, t) : (dist <= maxRange);
      const activelyFighting = inRange || (u.isEngaged && dist <= maxRange + 36);
      if (!activelyFighting) continue;

      const wX = u.team === 'western' ? uX : tX;
      const eX = u.team === 'eastern' ? uX : tX;
      const midX = (wX + eX) * 0.5;
      let distToFront = Math.abs(midX - dividerX);
      // Hysteresis: if a combat center was tracked recently, give it a sticky affinity (-35px)
      // so two nearby skirmishes do not flip back-and-forth every frame.
      if (this._lastCameraCombatMidX !== undefined && Math.abs(midX - this._lastCameraCombatMidX) < 140) {
        distToFront -= 35;
      }
      if (distToFront < bestPairDist) {
        bestPairDist = distToFront;
        leadWestX = Math.min(wX, eX);
        leadEastX = Math.max(wX, eX);
      }
    }

    // Also check if either planetary fortress turret is actively firing at an enemy unit in range
    if (leadWestX === null && this.bases) {
      for (const baseTeam of ['western', 'eastern']) {
        const base = this.bases[baseTeam];
        if (!base || base.hp <= 0) continue;
        const enemyTeam = baseTeam === 'western' ? 'eastern' : 'western';
        for (let i = 0; i < this.units.length; i++) {
          const eu = this.units[i];
          if (!eu || eu.isDead || eu.team !== enemyTeam || eu.isCinematic || eu.isGoldenStatue || eu.isParachuting) continue;
          const inBaseRange = this.isInRange ? this.isInRange(base, eu) : (Math.abs(eu.x - base.x) <= (base.range || 312) + (base.radius || 38) + (eu.radius || 20));
          if (inBaseRange) {
            const bX = getEntityCombatX(base);
            leadWestX = Math.min(bX, eu.x);
            leadEastX = Math.max(bX, eu.x);
            break;
          }
        }
        if (leadWestX !== null) break;
      }
    }

    if (leadWestX === null || leadEastX === null) {
      this._lastCameraCombatMidX = undefined;
      return out;
    }
    this._lastCameraCombatMidX = (leadWestX + leadEastX) * 0.5;

    // Expand slightly (up to 95px behind each frontline pole) to include immediate squadmates
    // fighting right alongside the lead unit without letting distant rear artillery pull the frame away.
    let clusterWestX = leadWestX;
    let clusterEastX = leadEastX;
    const SQUAD_CLUSTER_RADIUS = 95;
    for (let i = 0; i < this.units.length; i++) {
      const u = this.units[i];
      if (!u || u.isDead || u.isCinematic || u.isGoldenStatue || u.isCocoon || u.isParachuting) continue;
      if (!u.target || u.target.isDead) continue;
      if (u.team === 'western' && u.x < clusterWestX && (leadWestX - u.x) <= SQUAD_CLUSTER_RADIUS) {
        clusterWestX = u.x;
      } else if (u.team === 'eastern' && u.x > clusterEastX && (u.x - leadEastX) <= SQUAD_CLUSTER_RADIUS) {
        clusterEastX = u.x;
      }
    }

    out.inCombat = true;
    out.leftCombatX = clusterWestX;
    out.rightCombatX = clusterEastX;
    if (out.myTeam === 'Eastern' || out.myTeam === 'eastern') {
      out.attackerX = leadEastX;
      out.defenderX = leadWestX;
    } else {
      out.attackerX = leadWestX;
      out.defenderX = leadEastX;
    }
    return out;
  }

  // Detects if any unit has crossed the frontline or territory divider (or if the
  // frontline has already shifted away from midline). Used by mobile camera intro to
  // hand over immediately to the active frontline tracking perspective.
  hasUnitCrossedLine() {
    const midX = this.width / 2;
    if (Math.abs(this.territoryDivider - midX) > 2) return true;
    for (const u of this.units) {
      if (u.isDead || u.isStructure || u.isCinematic) continue;
      if (u.team === 'western' && u.x >= midX) return true;
      if (u.team === 'eastern' && u.x <= midX) return true;
    }
    return false;
  }

  initBases() {
    const self = this;
    this.bases = {
      western: {
        id: 'base_western',
        name: "Western Planetary Fortress",
        race: "Human",
        team: "western",
        x: Battlefield.BASE_EDGE_X, // Shifted toward the left edge, but clear of the fortress sprite's outward render bounds
        y: this.laneY + this.laneHeight / 2 + 4, // 340 (shifted 4px vertically down)
        radius: 38,
        maxHp: 1500,
        hp: 1500,
        damage: BALANCE.baseDamage,
        range: 312, // Exactly matches furthest shooting units (Nova & Tempest: 312px)
        attackCooldown: 0,
        attackSpeed: 1.4,
        isStructure: true,
        // Bases are targeted like units, so they must expose the same combat-facing
        // fields (buffs/isDead/shields) or on-hit effects such as Concussive Shells crash.
        isDead: false,
        buffs: [],
        shields: 0,
        maxShields: 0, // raised by the Aegis Generator building
        isAir: false,
        targetsGround: true,
        targetsAir: true,
        attributes: ["Armored", "Mechanical", "Structure"],
        // Signature aligned with Unit.takeDamage() (docs/UNIT_MVP_PLAN.md:
        // base damage now counts toward the attacker's Unit MVP score too) -
        // this also fixes a pre-existing mismatch where callers using the
        // full Unit-style argument list (e.g. Battlefield._resolveProjectileImpact())
        // had their real isGold value silently swallowed because it landed
        // past this function's old 3-parameter signature.
        takeDamage: function(amount, particles, attackerProjectileType = 'Bullet', showDamageText = true, isGoldAttacker = false, attacker = null, isReflected = false) {
          // Aegis Generator shield: drained first, same order as Unit.takeDamage.
          if (this.shields > 0) {
            if (this.shields >= amount) {
              this.shields -= amount;
              self.stats?.recordDamage('eastern', amount);
              self.stats?.recordBaseHit?.('western', amount, this.hp);
              if (attacker) self.stats?.recordUnitDamage(attacker.id, amount);
              particles?.addDamageText?.(this.x + 24, this.y, Math.round(amount), 'shield');
              return;
            }
            amount -= this.shields;
            self.stats?.recordDamage('eastern', this.shields);
            self.stats?.recordBaseHit?.('western', this.shields, this.hp);
            if (attacker) self.stats?.recordUnitDamage(attacker.id, this.shields);
            this.shields = 0;
          }
          // Armor was removed as a mechanic - a base's own shields
          // (Aegis Generator) are still drained first above, but once
          // those are gone every hit lands for its full amount.
          const eff = amount;
          this.hp = Math.max(0, this.hp - eff);
          self.stats?.recordDamage('eastern', eff);
          self.stats?.recordBaseHit?.('western', eff, this.hp);
          if (attacker) self.stats?.recordUnitDamage(attacker.id, eff);
          particles?.addDamageText?.(this.x + 24, this.y, Math.round(eff), isGoldAttacker ? 'gold' : 'crit');
          if (this.team === (self.game?.myTeam || 'western')) {
            self.game?.audio?.playBaseUnderAttack?.({ x: this.x });
          }
          if (this.hp <= 0 && !self.isGameOver) {
            self.hp = 0;
            self.winner = 'eastern';
            self.isGameOver = true;
            self.game?.audio?.playBaseDestruction?.({ x: this.x });
            self.game?.audio?.setMatchTension?.(0);
          }
        }
      },
      eastern: {
        id: 'base_eastern',
        name: "Eastern Planetary Fortress",
        race: "Human",
        team: "eastern",
        x: this.width - Battlefield.BASE_EDGE_X, // Shifted toward the right edge, but clear of the fortress sprite's outward render bounds
        y: this.laneY + this.laneHeight / 2 + 4, // 340 (shifted 4px vertically down)
        radius: 38,
        maxHp: 1500,
        hp: 1500,
        damage: BALANCE.baseDamage,
        range: 312, // Exactly matches furthest shooting units (Nova & Tempest: 312px)
        attackCooldown: 0,
        attackSpeed: 1.4,
        isStructure: true,
        // Bases are targeted like units, so they must expose the same combat-facing
        // fields (buffs/isDead/shields) or on-hit effects such as Concussive Shells crash.
        isDead: false,
        buffs: [],
        shields: 0,
        maxShields: 0, // raised by the Aegis Generator building
        isAir: false,
        targetsGround: true,
        targetsAir: true,
        attributes: ["Armored", "Mechanical", "Structure"],
        takeDamage: function(amount, particles, attackerProjectileType = 'Bullet', showDamageText = true, isGoldAttacker = false, attacker = null, isReflected = false) {
          if (this.shields > 0) {
            if (this.shields >= amount) {
              this.shields -= amount;
              self.stats?.recordDamage('western', amount);
              self.stats?.recordBaseHit?.('eastern', amount, this.hp);
              if (attacker) self.stats?.recordUnitDamage(attacker.id, amount);
              particles?.addDamageText?.(this.x - 24, this.y, Math.round(amount), 'shield');
              return;
            }
            amount -= this.shields;
            self.stats?.recordDamage('western', this.shields);
            self.stats?.recordBaseHit?.('eastern', this.shields, this.hp);
            if (attacker) self.stats?.recordUnitDamage(attacker.id, this.shields);
            this.shields = 0;
          }
          // Armor was removed as a mechanic - a base's own shields
          // (Aegis Generator) are still drained first above, but once
          // those are gone every hit lands for its full amount.
          const eff = amount;
          this.hp = Math.max(0, this.hp - eff);
          self.stats?.recordDamage('western', eff);
          self.stats?.recordBaseHit?.('eastern', eff, this.hp);
          if (attacker) self.stats?.recordUnitDamage(attacker.id, eff);
          particles?.addDamageText?.(this.x - 24, this.y, Math.round(eff), isGoldAttacker ? 'gold' : 'crit');
          if (this.team === (self.game?.myTeam || 'western')) {
            self.game?.audio?.playBaseUnderAttack?.({ x: this.x });
          }
          if (this.hp <= 0 && !self.isGameOver) {
            self.hp = 0;
            self.winner = 'western';
            self.isGameOver = true;
            self.game?.audio?.playBaseDestruction?.({ x: this.x });
            self.game?.audio?.setMatchTension?.(0);
          }
        }
      }
    };
  }

  // Where the base's artillery emplacement stands on the ground beside the
  // fortress, relative to base.x/base.y and mirrored per team. Since the
  // roof is now the foundation module's docking plane (see
  // js/engine/modules.js), the twin turret no longer sits on top of the
  // building - it is deployed next to it, on the lane floor.
  //
  // These two scalars are the SINGLE source of truth for that position: the
  // renderer draws there and updateBaseDefense() spawns its muzzle flash and
  // measures its shell flight from there. Before modules, the same numbers
  // were hardcoded twice (renderer.js and updateBaseDefense) and could drift
  // apart unnoticed - same reasoning as BuildingSystem.getAttachmentPosition().
  //
  // The turret stays part of the base object: it has no HP, cannot be
  // targeted separately and is not an obstacle, so unit targeting, range
  // checks, obstacle avoidance and collision resolution are all unaffected.
  getBaseTurretX(team) {
    const base = this.bases[team];
    const dir = team === 'western' ? 1 : -1;
    if (base?.race === 'Sul') {
      return base.x + dir * Battlefield.SUL_APEX_DX;
    }
    return base.x + dir * Battlefield.BASE_TURRET_DX;
  }

  getBaseTurretY(team) {
    const base = this.bases[team];
    if (base?.race === 'Sul') {
      return base.y + Battlefield.SUL_APEX_DY;
    }
    return this.bases[team].y + Battlefield.BASE_TURRET_DY;
  }

  set economy(eco) {
    this._economy = eco;
    if (eco && !eco.battlefield) {
      eco.battlefield = this;
    }
  }

  get economy() {
    return this._economy;
  }

  reset() {
    this.units = [];
    this.structures = [];
    this.goldenStatues = [];
    this.cocoons = [];
    this.metamorphosisFogTimer = 0;
    this.metamorphosisFogTeam = null;
    this.unitMap = new Map();
    this.detectors = { western: [], eastern: [] };
    this.pendingProjectiles = [];
    this.pendingCapsules = [];
    this.pendingDrones = [];
    this.deployBeacons = [];
    this.unitIdCounter = 0;
    this.tickCount = 0;
    this.initBases();
    // After initBases(): the fresh bases start at their card-free maxHp, and
    // the module stack that raised it is dropped along with them.
    this.moduleSystem?.reset();
    this.winner = null;
    this.isGameOver = false;
    this.isNuked = false;
    this.nukeTimer = 0;
    this.radioactiveCraters = [];
    this.scarType = null;
    this.frameEvents = [];
    this.territoryControl = 'neutral';
    this.territoryDivider = this.width / 2;
    this.territoryBonus = 0;
    this.territoryStillstandTimer = 0;
    this.isTerritoryLocked = false;
    this.territoryLockedTeam = null;
    this.activePrecisionStrikes = [];
    this.activeRetreatSignals = [];
    this.stats?.reset();
    this._lastMatchTension = undefined;
    this.game?.audio?.setMatchTension?.(0);
    // Campaign-only (docs/game-design-singleplayer-kampagne.md): suppresses
    // base rendering (js/engine/renderer.js) for an isolated squad-fight
    // encounter. Reset here so it defaults to false for every match type
    // and Game.startCampaignMission() only has to opt in, never opt out.
    this.hideBases = false;
    // Campaign-only (docs/singleplayer implementation soft-foraging-
    // flamingo.md): suppresses the per-unit HP bar ("leuchtender Streifen")
    // for a lightweight, non-"vollwertiger Kampf" encounter - same
    // opt-in-only reset pattern as hideBases above.
    this.hideHealthBars = false;
  }

  addDeployBeacon(team, card) {
    if (!this.bases?.[team]) return;
    const base = this.bases[team];
    const sign = team === 'western' ? 1 : -1;
    const x = base.x + sign * 145;
    const y = this.laneY + this.laneHeight / 2;
    this.deployBeacons = this.deployBeacons || [];
    this.deployBeacons.push({
      x,
      y,
      team,
      race: card?.race || (team === 'western' ? this.bases.western?.race : this.bases.eastern?.race) || 'Human',
      timer: 1.2,
      maxTime: 1.2
    });
  }

  addFrameEvent(event) {
    if (this.frameEvents.length < 256) {
      this.frameEvents.push(event);
    }
  }

  consumeFrameEvents() {
    if (this.frameEvents.length === 0) return [];
    const events = this.frameEvents;
    this.frameEvents = [];
    return events;
  }

  nextUnitId() {
    return 'unit_' + (this.unitIdCounter++);
  }

  // Every Unit must be constructed with a battlefield-assigned id and a live
  // battlefield reference - see docs/INVARIANTS.md "Unit-Spawn-Vertrag". Use
  // this instead of `new Unit(...)` directly so that isn't something every
  // call site has to remember; id/battlefield are spread in last so a caller
  // can't accidentally override them via extraOptions (unless explicitly passing extraOptions.id for network reconciliation).
  spawnRaw(cardData, team, x, y, extraOptions = {}) {
    const id = extraOptions.id || this.nextUnitId();
    const unit = new Unit(cardData, team, x, y, { rng: this.rng, ...extraOptions, id, battlefield: this });
    this.units.push(unit);
    this.unitMap.set(unit.id, unit);
    if (unit.isStructure) {
      this.structures.push(unit);
    } else {
      // Unit MVP Spotlight (docs/UNIT_MVP_PLAN.md): structures are excluded
      // by simply never registering them here.
      this.stats?.registerUnit(unit.id, cardData.cost || 0, team, cardData);
    }
    if (unit.isGoldenStatue && this.goldenStatues && !this.goldenStatues.includes(unit)) {
      this.goldenStatues.push(unit);
      if (unit.isStickman && !unit.hasGoldPedestal) {
        this.enforceDeadStickmanLimit(this.particles, 3);
      }
    }
    if (unit.isDetector && this.detectors?.[team]) {
      this.detectors[team].push(unit);
    }
    return unit;
  }

  // O(1) id -> Unit lookup for the normal spawnRaw() path, with a linear
  // fallback for units some tests construct directly (`new Unit(..., battlefield)`
  // + `battlefield.units.push()`) and never register in unitMap.
  getUnitById(id) {
    return this.unitMap.get(id) || this.units.find(u => u.id === id) || null;
  }

  // Like getUnitById(), but also resolves the two base objects - needed by
  // tether-style mechanics (Sul Harvesting Pillar) whose lock-on target can
  // be a base, which never lives in this.units/unitMap.
  getUnitOrBaseById(id) {
    if (this.bases.western.id === id) return this.bases.western;
    if (this.bases.eastern.id === id) return this.bases.eastern;
    return this.getUnitById(id);
  }

  transferUnit(unit, newTeam) {
    if (!unit || unit.team === newTeam) return;
    const oldTeam = unit.team;
    unit.team = newTeam;
    if (unit.isDetector && this.detectors) {
      const oldList = this.detectors[oldTeam];
      if (oldList) {
        const idx = oldList.indexOf(unit);
        if (idx !== -1) oldList.splice(idx, 1);
      }
      const newList = this.detectors[newTeam];
      if (newList && !newList.includes(unit)) {
        newList.push(unit);
      }
    }
    if ((unit.cardData?.cost || 0) >= 40 || unit.isMelt || unit.cardData?.type === 'Melt') {
      this.stats?.recordHeavyUnitHijack?.(newTeam);
    }
    this.stats?.transferUnit?.(unit.id, newTeam);
  }

  addRadioactiveCrater(x, y) {
    this.isNuked = true;
    this.nukeTimer = 0;
    this.scarType = 'nuclear';
    this.radioactiveCraters.push({
      x,
      y,
      radius: 120,
      age: 0,
      kind: 'crater'
    });
  }

  addObsidianGlassField(x, y) {
    this.isNuked = true;
    this.nukeTimer = 0;
    this.scarType = 'bioplasma';
    this.radioactiveCraters.push({
      x,
      y,
      radius: 110,
      age: 0,
      kind: 'glass'
    });
  }

  spawnUnit(card, team, edition = null, customAuraHex = null) {
    const spawnCount = card.spawnCount || 1;
    const base = this.bases[team];
    const sign = team === 'western' ? 1 : -1;

    // Distribute across the full 320px lane height (laneY + 40 to laneY + laneHeight - 40)
    const laneTop = this.laneY + 40;
    const usableHeight = this.laneHeight - 80;

    let lastSpawned = null;
    for (let i = 0; i < spawnCount; i++) {
      let offsetX, offsetY;
      if (card.deployAtBonusIndicator || card.unitType === 'DeployableCannon' || card.name === 'Deployable Cannon') {
        const dividerX = (this.territoryDivider !== undefined && this.territoryDivider !== null) ? this.territoryDivider : (this.width / 2);
        if (card.unitType === 'ClaimingPillar' || card.name === 'Claiming Pillar') {
          // Claiming Pillar spawns EXACTLY on the territoryDivider X coordinate
          const margin = Battlefield.BASE_EDGE_X + 48;
          offsetX = Math.max(margin, Math.min(this.width - margin, dividerX));
        } else {
          // Deployable Cannon / Monitor Lock deploy slightly behind the line on their team's side
          offsetX = team === 'western'
            ? Math.max(base.x + 130, Math.min(dividerX - 25, this.width - 160))
            : Math.min(base.x - 130, Math.max(dividerX + 25, 160));
        }
        offsetY = laneTop + this.rng.next() * usableHeight;
      } else {
        offsetX = base.x + sign * this.rng.range(130, 160);
        if (spawnCount > 1) {
          // Multi-unit squad: distribute evenly across vertical lanes
          const step = usableHeight / Math.max(1, spawnCount - 1);
          offsetY = laneTop + i * step + this.rng.range(-8, 8);
        } else {
          // Single unit: randomize across top, center, and bottom corridors
          offsetY = laneTop + this.rng.next() * usableHeight;
        }
      }
      offsetY = Math.max(this.laneY + 35, Math.min(this.laneY + this.laneHeight - 35, offsetY));

      const spawnedUnit = this.spawnRaw(card, team, offsetX, offsetY, {
        ...(edition ? { edition } : {}),
        ...(customAuraHex ? { customAuraHex } : {})
      });
      lastSpawned = spawnedUnit;

      // Claiming Pillar immediate unit displacement (verdrängt Einheiten)
      if (spawnedUnit && spawnedUnit.isClaimingPillar && this.units) {
        const pushRadius = spawnedUnit.radius + 6;
        for (let ui = 0; ui < this.units.length; ui++) {
          const u = this.units[ui];
          if (u === spawnedUnit || u.isDead || u.isStructure || u.isGoldenStatue || u.isAir) continue;
          const dx = u.x - spawnedUnit.x;
          const dy = u.y - spawnedUnit.y;
          const minD = pushRadius + u.radius;
          const distSq = dx * dx + dy * dy;
          if (distSq < minD * minD && distSq > 0.0001) {
            const d = Math.sqrt(distSq);
            const overlap = (minD - d) / d;
            u.x += dx * overlap;
            u.y += dy * overlap;
          }
        }
      }
    }
    return lastSpawned;
  }

  spawnUnitByName(cardName, team, customX = null, customY = null) {
    const card = getCardByName(cardName);
    if (!card) return null;
    if (customX !== null && customY !== null) {
      return this.spawnRaw(card, team, customX, customY);
    } else {
      return this.spawnUnit(card, team);
    }
  }

  addProjectileSim(config) {
    this.pendingProjectiles.push({
      ticksRemaining: Math.max(1, Math.round(config.flightTicks || 1)),
      targetX: config.targetX,
      targetY: config.targetY,
      targetUnitId: config.targetUnitId || null,
      isStructureTarget: !!config.isStructureTarget,
      targetBaseTeam: config.targetBaseTeam || null,
      damage: config.damage || 0,
      splashRadius: config.splashRadius || 0,
      splashDamage: config.splashDamage !== undefined ? config.splashDamage : undefined,
      team: config.team,
      projectileType: config.projectileType || 'Bullet',
      onImpact: config.onImpact || null,
      sourceUnit: config.sourceUnit || null,
      isGoldAttacker: !!config.isGoldAttacker,
      hasIncinerate: !!config.hasIncinerate,
      incinerateDuration: config.incinerateDuration || 5,
      incinerateDps: config.incinerateDps || 2,
      isReflected: !!config.isReflected,
      hasSporeSlow: !!config.hasSporeSlow,
      sporeSlowDuration: config.sporeSlowDuration || 10,
      sporeSlowAmount: config.sporeSlowAmount || 2,
      hasDisarm: !!config.hasDisarm,
      disarmDuration: config.disarmDuration || 3
    });
  }

  _resolveProjectileImpact(p, particles, audio) {
    if (p.isIntercepted) {
      return;
    }

    const { targetX, targetY, targetUnitId, isStructureTarget, targetBaseTeam, damage, splashRadius, splashDamage, team, projectileType, onImpact, sourceUnit, isGoldAttacker, hasIncinerate, incinerateDuration, incinerateDps, isReflected, hasSporeSlow, sporeSlowDuration, sporeSlowAmount, hasDisarm, disarmDuration } = p;
    
    if (typeof onImpact === 'function') {
      onImpact(particles, audio);
      return;
    }

    let target = null;
    if (targetUnitId) {
      target = this.getUnitOrBaseById ? this.getUnitOrBaseById(targetUnitId) : this.getUnitById(targetUnitId);
    }
    if (!target && targetBaseTeam && this.bases[targetBaseTeam]) {
      target = this.bases[targetBaseTeam];
    }

    const isGold = !!(isGoldAttacker || sourceUnit?.edition === 'gold');

    if (splashRadius > 0) {
      const impactX = (target && !target.isDead) ? target.x : targetX;
      const impactY = (target && !target.isDead) ? target.y : targetY;
      if (target && !target.isDead) {
        target.takeDamage(damage, particles, projectileType, true, isGold, sourceUnit, isReflected);
        if (hasIncinerate && typeof target.applyIncinerate === 'function') {
          target.applyIncinerate(incinerateDuration, incinerateDps, team);
        }
        if (hasSporeSlow && typeof target.applySporeSlow === 'function') {
          target.applySporeSlow(sporeSlowDuration, sporeSlowAmount);
        }
        if (hasDisarm && typeof target.applyDisarm === 'function') {
          target.applyDisarm(disarmDuration);
        }
      }
      const hitAir = sourceUnit ? (sourceUnit.targetsAir !== false) : true;
      const hitGround = sourceUnit ? (sourceUnit.targetsGround !== false) : true;
      const effectiveSplashDmg = splashDamage !== undefined ? splashDamage : damage;
      this.dealSplashDamage(impactX, impactY, splashRadius, effectiveSplashDmg, team, hitAir, hitGround, particles, projectileType, target, false, sourceUnit, hasIncinerate, incinerateDuration, incinerateDps);
      if (projectileType !== 'Grenade' && projectileType !== 'Rocket') {
        particles?.addExplosion?.(impactX, impactY, 22, '#ff8800', 12);
      }
      particles?.addScorchMark?.(impactX, impactY, 14);
      particles?.addArtillerySpall?.(impactX, impactY);
    } else {
      if (target && !target.isDead) {
        target.takeDamage(damage, particles, projectileType, true, isGold, sourceUnit, isReflected);
        if (hasIncinerate && typeof target.applyIncinerate === 'function') {
          target.applyIncinerate(incinerateDuration, incinerateDps, team);
        }
        if (hasSporeSlow && typeof target.applySporeSlow === 'function') {
          target.applySporeSlow(sporeSlowDuration, sporeSlowAmount);
        }
        if (hasDisarm && typeof target.applyDisarm === 'function') {
          target.applyDisarm(disarmDuration);
        }
      }
    }
  }

  _resolveCapsuleLanding(c, particles, audio) {
    const { team, targetX, targetY, structureCardData } = c;
    
    // 1. Splash damage on impact (Panzer shot power: 40 dmg, 60px radius)
    const splashDamage = structureCardData?.capsuleDamage || structureCardData?.damage || 40;
    const splashRadius = (structureCardData?.capsuleSplashRadius || structureCardData?.splashRadius || 2.5) * 24;
    this.dealSplashDamage(targetX, targetY, splashRadius, splashDamage, team, false, true, particles, 'Grenade');
    if (audio?.playCapsuleImpact) {
      audio.playCapsuleImpact({ x: targetX });
    } else {
      audio?.playExplosion?.('heavy', { x: targetX });
    }

    // 2. Deploy passengers as a tightly clustered tactical squad at impact point
    const passengerCard = getCardByName(structureCardData?.capsuleUnit || 'Marine');
    if (passengerCard) {
      const count = structureCardData?.capsuleLoadCount || 3;
      const dir = team === 'western' ? 1 : -1;
      const offsets = [
        { dx: 0, dy: 0 },
        { dx: -dir * 12, dy: -12 },
        { dx: -dir * 12, dy: 12 }
      ];
      for (let i = 0; i < count; i++) {
        const off = offsets[i] || { dx: -dir * (i * 12), dy: ((i % 2 === 0 ? 1 : -1) * 14) };
        const spawnX = targetX + off.dx;
        const spawnY = Math.max(this.laneY + 35, Math.min(this.laneY + this.laneHeight - 35, targetY + off.dy));
        this.spawnRaw(passengerCard, team, spawnX, spawnY);
      }
      audio?.playBark?.(passengerCard.race, 'spawn', { x: targetX });
    }
  }

  // Invasion Capsule Sling: once the module's capsule is full (see
  // ModuleSystem.tickCapsuleSling() in js/engine/modules.js), it catapults
  // the capsule toward the frontline - the same "level of the bonus
  // indicator" spot Deployable Cannon deploys itself onto in spawnUnit()
  // above - instead of spawning its passengers right next to the launcher.
  // The 3 Marines only actually exist once the capsule visibly lands.
  //
  // Takes an explicit origin rather than a launcher Unit: the Sling is a
  // base module now (it has no Unit at all) and the capsule leaves from the
  // module's slot up in the tower.
  launchInvasionCapsule(team, originX, originY, cardData, particles, audio) {
    const base = this.bases[team];
    const enemyTeam = team === 'western' ? 'eastern' : 'western';

    // Target the frontmost enemy unit (prioritizing ground units for splash damage efficiency)
    let frontmostEnemy = null;
    let minForwardDist = Infinity;
    let fallbackAirEnemy = null;
    let minAirDist = Infinity;

    for (const u of this.units) {
      if (u.team !== enemyTeam || u.isDead) continue;
      if (u.isCloaked && !this.isPositionDetected(team, u.x, u.y)) continue;
      const forwardDist = team === 'western' ? (u.x - base.x) : (base.x - u.x);
      if (!u.isAir) {
        if (forwardDist < minForwardDist) {
          minForwardDist = forwardDist;
          frontmostEnemy = u;
        }
      } else {
        if (forwardDist < minAirDist) {
          minAirDist = forwardDist;
          fallbackAirEnemy = u;
        }
      }
    }

    const targetEnemy = frontmostEnemy || fallbackAirEnemy;
    let targetX, targetY;

    if (targetEnemy) {
      targetX = Math.max(100, Math.min(this.width - 100, targetEnemy.x));
      targetY = Math.max(this.laneY + 35, Math.min(this.laneY + this.laneHeight - 35, targetEnemy.y));
    } else {
      // Fallback: frontline territory spot if no living enemy units on the field
      const dividerX = (this.territoryDivider !== undefined && this.territoryDivider !== null) ? this.territoryDivider : (this.width / 2);
      targetX = team === 'western'
        ? Math.max(base.x + 80, Math.min(dividerX - 25, this.width - 120))
        : Math.min(base.x - 80, Math.max(dividerX + 25, 120));
      // Mid-lane, not the launch origin's own y - the module sits high up in
      // the tower, well above the lane the capsule is meant to land in.
      targetY = base.y;
    }

    const dist = Math.hypot(targetX - originX, targetY - originY);
    const flightTimeSec = Math.max(0.9, Math.min(1.6, dist / 550));
    const flightTicks = Math.max(1, Math.round(flightTimeSec * 60));

    if (audio?.playCapsuleLaunch) {
      audio.playCapsuleLaunch({ x: originX });
    } else {
      audio?.playExplosion?.('small', { x: originX });
    }
    particles?.addInvasionCapsule?.(originX, originY, targetX, targetY, () => {});

    this.pendingCapsules.push({
      ticksRemaining: flightTicks,
      team,
      targetX,
      targetY,
      structureCardData: cardData
    });
  }

  // Drone Command: launches an explosive suicide drone toward targetEnemy.
  // The drone flies dynamically towards the target, re-samples the target's position
  // once per second (every 60 ticks) to correct its route, retargets if the unit dies,
  // cannot be deflected or shot at, and detonates upon reaching the target position.
  launchSuicideDrone(team, originX, originY, targetEnemy, cardData, particles = null, audio = null) {
    const targetX = targetEnemy ? targetEnemy.x : (team === 'western' ? this.width - 100 : 100);
    const targetY = targetEnemy ? targetEnemy.y : (this.laneY + this.laneHeight / 2);

    const speedPx = (cardData?.droneSpeed || 10) * 26; // 260 px/sec
    const rangePx = (cardData?.droneRange || 50) * 24; // 1200 px

    if (audio?.playDroneLaunch) {
      audio.playDroneLaunch({ x: originX });
    } else {
      audio?.playLaser?.('small', { x: originX });
    }

    const droneObj = {
      team,
      originX,
      originY,
      x: originX,
      y: originY,
      speedPx,
      rangePx,
      targetEnemy,
      targetX,
      targetY,
      retrackTicks: 60, // 1-second countdown at 60 FPS
      launchTicks: 20, // 0.33s liftoff acceleration & pad clearance
      cardData,
      isDetonated: false
    };

    const visualDrone = particles?.addSuicideDrone?.(originX, originY, targetX, targetY, team, 999, () => {}, droneObj);
    droneObj.visualDrone = visualDrone;

    this.pendingDrones.push(droneObj);
  }

  _resolveDroneImpact(d, particles = null, audio = null) {
    const { team, cardData } = d;
    const impactX = d.x !== undefined ? d.x : d.targetX;
    const impactY = d.y !== undefined ? d.y : d.targetY;
    const damage = cardData?.droneDamage ?? cardData?.damage ?? 20;
    const splashRadius = (cardData?.droneSplashRadius || 1.8) * 24; // ~43px

    // Mini-splash damage: hits Ground (true), ignores Air (false)
    this.dealSplashDamage(impactX, impactY, splashRadius, damage, team, false, true, particles, 'Grenade');
    if (audio?.playDroneDetonation) {
      audio.playDroneDetonation({ x: impactX });
    } else {
      audio?.playExplosion?.('small', { x: impactX });
    }
  }

  _resolveCollisionPair(u1, u2) {
    if (!u1 || !u2 || u1.isDead || u2.isDead || u1.isParachuting || u2.isParachuting) return;
    if (!Number.isFinite(u1.x) || !Number.isFinite(u1.y) || !Number.isFinite(u2.x) || !Number.isFinite(u2.y)) return;

    const dx = u2.x - u1.x;
    const dy = u2.y - u1.y;
    const isSameTeam = (u1.team === u2.team);
    const isBurrowed1 = !!(u1.isBurrowed || u1.mineState === 'burrowed' || u1.mineState === 'burrowing' || u1.mineState === 'rising' || u1.isMineRising);
    const isBurrowed2 = !!(u2.isBurrowed || u2.mineState === 'burrowed' || u2.mineState === 'burrowing' || u2.mineState === 'rising' || u2.isMineRising);
    if (isBurrowed1 || isBurrowed2) return;

    const isImmovable1 = u1.isStructure || u1.isGoldenStatue || u1.isCocoon || !Number.isFinite(u1.mass);
    const isImmovable2 = u2.isStructure || u2.isGoldenStatue || u2.isCocoon || !Number.isFinite(u2.mass);

    if (isImmovable1 && isImmovable2) return;

    const minDist = isSameTeam && !isImmovable1 && !isImmovable2
      ? ((u1.radius + u2.radius) * 0.75 + 2)
      : (u1.radius + u2.radius + 4);

    if (Math.abs(dx) >= minDist || Math.abs(dy) >= minDist) return;
    const distSq = dx * dx + dy * dy;
    if (distSq < minDist * minDist && distSq > 0.000001) {
      const dist = Math.sqrt(distSq);
      const overlap = (minDist - dist) / dist;
      let forceX = dx * overlap * 0.35;
      let forceY = dy * overlap * 0.35;

      const maxDisp = 2.5;

      if (isImmovable2 && !isImmovable1) {
        if (u2.isStructure && u2.isMonitorLock && u2.isGrowing && u1.team !== u2.team && !u1.isDead) {
          const isMassiveOrStructure = u1.isStructure || u1.cardData?.attributes?.includes('Massive') || u1.attributes?.includes('Massive');
          const crushDmg = isMassiveOrStructure ? 150 : (u1.hp + 9999);
          u1.takeDamage(crushDmg, this.particles, 'Melee', true, u2.edition === 'gold', u2);
        }
        // u2 is an immovable structure or golden statue: u1 absorbs 100% of displacement
        const isStickmanObstacle = !!(u2.isStickman && u2.isGoldenStatue);
        const isStuck = (u1._stuckTicks && u1._stuckTicks > 10);

        // Map-edge pinch & clearance detection (prevent wedging between obstacle and map edges)
        const laneTop = this.laneY;
        const laneBottom = this.laneY + this.laneHeight;
        const u1R = u1.radius || 17;
        const u2R = u2.radius || 24;
        const gapAbove = (u2.y - u2R) - laneTop;
        const gapBelow = laneBottom - (u2.y + u2R);
        const u1Diameter = u1R * 2;
        const isTopPinch = (gapAbove < u1Diameter + 6);
        const isBottomPinch = (gapBelow < u1Diameter + 6);

        // Determine optimal sliding deflection direction
        let steerUp;
        if (isTopPinch) {
          steerUp = false; // Choke point at top edge: strictly deflect DOWN into open space
        } else if (isBottomPinch) {
          steerUp = true;  // Choke point at bottom edge: strictly deflect UP into open space
        } else if (u1.targetY !== undefined && Math.abs(u1.targetY - u2.y) > 2) {
          steerUp = (u1.targetY < u2.y);
        } else if (Math.abs(dy) > 0.001) {
          steerUp = (u1.y < u2.y);
        } else {
          const charCode = (typeof u1.id === 'string' && u1.id.length > 0)
            ? (u1.id.charCodeAt(u1.id.length - 1) || 0)
            : 0;
          steerUp = (((charCode) % 2) === 0);
        }

        if (isStickmanObstacle || isTopPinch || isBottomPinch || Math.abs(dy) < 2.5 || isStuck) {
          // Kantenglättung (Edge Smoothing & Tangential Deflection):
          // Convert frontal collision force into decisive lateral slide around the obstacle contour
          const latForce = Math.max(Math.abs(forceY), (minDist - dist) * 0.40 + (isStuck ? 1.2 : 0.6));
          forceY = (steerUp ? 1 : -1) * latForce;

          // If trapped in a map-edge choke (e.g. u1 was above u2 near top edge), aggressively slide out
          if (isTopPinch && u1.y <= u2.y + 4) {
            forceY = -Math.max(latForce, 1.8); // u1.y -= forceY -> increases y (pushes down into open space)
          } else if (isBottomPinch && u1.y >= u2.y - 4) {
            forceY = Math.max(latForce, 1.8);  // u1.y -= forceY -> decreases y (pushes up into open space)
          }
        }

        // Soften frontal X-resistance (Kantenglättung: prevent blunt wall stop on edges)
        if (isStickmanObstacle || isStuck || Math.abs(u1.x - u2.x) < minDist * 0.95) {
          forceX *= (isStickmanObstacle ? 0.35 : 0.55);
        }

        u1.x -= Math.max(-maxDisp, Math.min(maxDisp, forceX));
        u1.y -= Math.max(-maxDisp, Math.min(maxDisp, forceY));
      } else if (isImmovable1 && !isImmovable2) {
        if (u1.isStructure && u1.isMonitorLock && u1.isGrowing && u2.team !== u1.team && !u2.isDead) {
          const isMassiveOrStructure = u2.isStructure || u2.cardData?.attributes?.includes('Massive') || u2.attributes?.includes('Massive');
          const crushDmg = isMassiveOrStructure ? 150 : (u2.hp + 9999);
          u2.takeDamage(crushDmg, this.particles, 'Melee', true, u1.edition === 'gold', u1);
        }
        // u1 is an immovable structure or golden statue: u2 absorbs 100% of displacement
        const isStickmanObstacle = !!(u1.isStickman && u1.isGoldenStatue);
        const isStuck = (u2._stuckTicks && u2._stuckTicks > 10);

        // Map-edge pinch & clearance detection
        const laneTop = this.laneY;
        const laneBottom = this.laneY + this.laneHeight;
        const u1R = u1.radius || 24;
        const u2R = u2.radius || 17;
        const gapAbove = (u1.y - u1R) - laneTop;
        const gapBelow = laneBottom - (u1.y + u1R);
        const u2Diameter = u2R * 2;
        const isTopPinch = (gapAbove < u2Diameter + 6);
        const isBottomPinch = (gapBelow < u2Diameter + 6);

        // Determine bypass deflection direction
        let steerUp;
        if (isTopPinch) {
          steerUp = false; // Choke point at top edge: strictly deflect DOWN into open space
        } else if (isBottomPinch) {
          steerUp = true;  // Choke point at bottom edge: strictly deflect UP into open space
        } else if (u2.targetY !== undefined && Math.abs(u2.targetY - u1.y) > 2) {
          steerUp = (u2.targetY < u1.y);
        } else if (Math.abs(dy) > 0.001) {
          steerUp = (u2.y < u1.y);
        } else {
          const charCode = (typeof u2.id === 'string' && u2.id.length > 0)
            ? (u2.id.charCodeAt(u2.id.length - 1) || 0)
            : 0;
          steerUp = (((charCode) % 2) === 0);
        }

        if (isStickmanObstacle || isTopPinch || isBottomPinch || Math.abs(dy) < 2.5 || isStuck) {
          // Kantenglättung (Edge Smoothing & Tangential Deflection):
          const latForce = Math.max(Math.abs(forceY), (minDist - dist) * 0.40 + (isStuck ? 1.2 : 0.6));
          forceY = (steerUp ? -1 : 1) * latForce;

          // Map-edge pinch escape: if unit is wedged between obstacle and boundary, slide firmly out
          if (isTopPinch && u2.y <= u1.y + 4) {
            forceY = Math.max(latForce, 1.8);  // u2.y += forceY -> increases Y (pushes down into open space)
          } else if (isBottomPinch && u2.y >= u1.y - 4) {
            forceY = -Math.max(latForce, 1.8); // u2.y += forceY -> decreases Y (pushes up into open space)
          }
        }

        // Soften frontal X-resistance (Kantenglättung: prevent blunt wall stop on edges)
        if (isStickmanObstacle || isStuck || Math.abs(u2.x - u1.x) < minDist * 0.95) {
          forceX *= (isStickmanObstacle ? 0.35 : 0.55);
        }

        u2.x += Math.max(-maxDisp, Math.min(maxDisp, forceX));
        u2.y += Math.max(-maxDisp, Math.min(maxDisp, forceY));
      } else {
        // Dynamic unit-vs-unit collision (Invariante #12: Etabliertes RTS-Massen- & Kollisionshandling)
        const marchDir = (u1.team === 'western' ? 1 : -1);

        // Effective masses with in-combat / stationary inertia anchor (guaranteed finite & positive)
        const m1 = Math.max(0.1, Number.isFinite(u1.mass) ? u1.mass : 1.0) * (u1.isEngaged ? 5.0 : 1.0);
        const m2 = Math.max(0.1, Number.isFinite(u2.mass) ? u2.mass : 1.0) * (u2.isEngaged ? 5.0 : 1.0);
        const totalMass = m1 + m2;
        const w1 = totalMass > 0 ? (m2 / totalMass) : 0.5; // displacement weight for u1
        const w2 = totalMass > 0 ? (m1 / totalMass) : 0.5; // displacement weight for u2

        if (isSameTeam) {
          // Anti-Bulldozing & Slip-Through Exceptions for friendly units:
          // 1. Anti-Bulldozing: Leading unit is NEVER pushed forward along the march axis into danger.
          // 2. Slip-Past Exception: Trailing unit is NOT braked along X (no forward freeze);
          //    it glides forward past the ally while lateral deflection fans them out in Y.
          // 3. Direction Harmonization: Lateral deflection force pushes in the direction of the unit's
          //    targetY bypass, preventing high-frequency jitter loops.
          if (Math.abs(dx) < 2.5) {
            // Units are abreast (side-by-side): separate symmetrically along Y without role-flipping or oscillation
            let u1Higher;
            if (Math.abs(u1.y - u2.y) > 0.5) {
              u1Higher = (u1.y < u2.y);
            } else {
              const id1 = typeof u1.id === 'string' ? u1.id : '';
              const id2 = typeof u2.id === 'string' ? u2.id : '';
              u1Higher = (id1 < id2);
            }
            const latForce = Math.min(maxDisp, (minDist - dist) * 0.4);
            const disp = latForce * 0.5;
            if (u1Higher) {
              u1.y -= disp;
              u2.y += disp;
            } else {
              u1.y += disp;
              u2.y -= disp;
            }
          } else {
            const u2AheadOfU1 = (dx * marchDir > 0);
            const leading = u2AheadOfU1 ? u2 : u1;
            const trailing = u2AheadOfU1 ? u1 : u2;

            let steerTrailingUp;
            if (trailing.targetY !== undefined && Math.abs(trailing.targetY - leading.y) > 2) {
              steerTrailingUp = trailing.targetY < leading.y;
            } else if (Math.abs(trailing.y - leading.y) > 0.5) {
              steerTrailingUp = trailing.y < leading.y;
            } else {
              const charCode = (typeof trailing.id === 'string' && trailing.id.length > 0)
                ? (trailing.id.charCodeAt(trailing.id.length - 1) || 0)
                : 0;
              steerTrailingUp = (charCode % 2) === 0;
            }

            const isStuck = (trailing._stuckTicks && trailing._stuckTicks > 12);
            const latForce = Math.min(maxDisp, (minDist - dist) * 0.4 + (isStuck ? 1.2 : 0.5));
            const trailingDispY = (steerTrailingUp ? -latForce : latForce);

            if (u2AheadOfU1) {
              u1.y += Math.max(-maxDisp, Math.min(maxDisp, trailingDispY));
              if (!leading.isEngaged) {
                u2.y -= Math.max(-maxDisp, Math.min(maxDisp, trailingDispY * 0.5));
              }
            } else {
              u2.y += Math.max(-maxDisp, Math.min(maxDisp, trailingDispY));
              if (!leading.isEngaged) {
                u1.y -= Math.max(-maxDisp, Math.min(maxDisp, trailingDispY * 0.5));
              }
            }
          }
        } else {
          // Hostile unit collision:
          const hp1 = u1.isHyperPanzer;
          const hp2 = u2.isHyperPanzer;
          if ((hp1 || hp2) && !(hp1 && hp2)) {
            const tank = hp1 ? u1 : u2;
            const enemy = hp1 ? u2 : u1;
            if (!enemy.isStructure && !enemy.isGoldenStatue && !enemy.isDead && !enemy.isUntargetable && !enemy.isAir) {
              const tankDir = (tank.team === 'western' ? 1 : -1);
              if (enemy.hp < 100) {
                // Overrun: Colliding with ground enemy < 100 HP destroys it immediately (crush damage, crunch sound, blood/debris)
                const killDamage = enemy.hp + 9999;
                const fx = this.particles;
                const audio = (typeof window !== 'undefined' && window.tcgGame?.audio) || this.particles?.audio || (typeof window !== 'undefined' && window.tcgSandbox?.audio) || this.audio || null;
                fx?.addBloodSplatter?.(enemy.x, enemy.y, 14, '#991b1b');
                fx?.addStructureDebris?.(enemy.x, enemy.y, 16);
                fx?.addHitSparks?.(enemy.x, enemy.y, 'physical', 10);
                if (audio?.playHyperPanzerCrush) {
                  audio.playHyperPanzerCrush({ x: enemy.x });
                } else if (audio?.playUnitDeathMachinery) {
                  audio.playUnitDeathMachinery({ x: enemy.x });
                } else {
                  audio?.playExplosion?.('small', { x: enemy.x });
                }
                enemy.takeDamage(killDamage, fx, 'Melee', true, tank.edition === 'gold', tank);
                return;
              } else {
                // Bulldoze: Colliding with ground enemy >= 100 HP pushes it forward in the tank's driving direction
                const pushDist = Math.max(1.5, Math.min(maxDisp * 2.0, (minDist - dist) * 0.7));
                enemy.x += tankDir * pushDist;
                if (Math.abs(dy) < 3.0) {
                  const steerUp = dy > 0 ? -1 : 1;
                  enemy.y += steerUp * 1.0;
                }
                return;
              }
            }
          }

          // Standard mass-proportional separation with displacement clamping
          // Deterministic lateral deflection when hostile units meet frontally
          let steerUp;
          if (Math.abs(dy) > 0.001) {
            steerUp = (u1.y < u2.y);
          } else {
            const charCode = (typeof u1.id === 'string' && u1.id.length > 0)
              ? (u1.id.charCodeAt(u1.id.length - 1) || 0)
              : 0;
            steerUp = (((charCode) % 2) === 0);
          }

          if (Math.abs(dy) < minDist * 0.5) {
            const latForce = Math.max(Math.abs(forceY), (minDist - dist) * 0.40 + 0.5);
            forceY = (steerUp ? 1 : -1) * latForce;
            // Soften frontal X-resistance to allow smooth lateral sliding past each other
            forceX *= 0.6;
          }

          let u1DispX = -forceX * w1;
          let u2DispX = forceX * w2;

          // Anti-Bulldozing for hostile collisions (Invariante #11):
          // A lighter hostile unit (m_lighter < m_heavier) must NEVER push a heavier unit
          // backwards along its march axis.
          const marchDir1 = marchDir;
          const marchDir2 = -marchDir1;
          const u1MovingBackwards = (u1DispX * marchDir1 < 0);
          const u2MovingBackwards = (u2DispX * marchDir2 < 0);

          if (m1 > m2 && u1MovingBackwards) {
            u1DispX = 0;
            u2DispX = forceX;
          } else if (m2 > m1 && u2MovingBackwards) {
            u2DispX = 0;
            u1DispX = -forceX;
          }

          u1.x += Math.max(-maxDisp, Math.min(maxDisp, u1DispX));
          u1.y += Math.max(-maxDisp, Math.min(maxDisp, -forceY * w1));
          u2.x += Math.max(-maxDisp, Math.min(maxDisp, u2DispX));
          u2.y += Math.max(-maxDisp, Math.min(maxDisp, forceY * w2));
        }
      }
    }
  }

  _processBucketCollisions(buckets, numBuckets) {
    if (!buckets) return;
    for (let cellX = 0; cellX < numBuckets; cellX++) {
      const list = buckets[cellX];
      if (!list) continue;
      const listLen = list.length;
      if (listLen === 0) continue;

      for (let i = 0; i < listLen; i++) {
        for (let j = i + 1; j < listLen; j++) {
          this._resolveCollisionPair(list[i], list[j]);
        }
      }

      if (cellX + 1 < numBuckets) {
        const nextList = buckets[cellX + 1];
        if (!nextList) continue;
        const nextLen = nextList.length;
        if (nextLen > 0) {
          for (let i = 0; i < listLen; i++) {
            for (let j = 0; j < nextLen; j++) {
              this._resolveCollisionPair(list[i], nextList[j]);
            }
          }
        }
      }
    }
  }

  resolveCollisions() {
    const CELL_SIZE = 100;
    const numBuckets = 20;
    if (!this._groundBuckets || this._groundBuckets.length < numBuckets) {
      this._groundBuckets = Array.from({ length: numBuckets }, () => []);
    }
    if (!this._airBuckets || this._airBuckets.length < numBuckets) {
      this._airBuckets = Array.from({ length: numBuckets }, () => []);
    }
    for (let b = 0; b < numBuckets; b++) {
      this._groundBuckets[b].length = 0;
      this._airBuckets[b].length = 0;
    }

    for (let uIdx = 0; uIdx < this.units.length; uIdx++) {
      const u = this.units[uIdx];
      if (!u || u.isDead || u.isParachuting || u.isCinematic) continue;
      // Buried units, crater holes and rising balloon spheres are anchored in bedrock / airborne
      // and do not collide with ground marchers (cannot be displaced by units running into them)
      if (u.isBurrowed || u.mineState === 'burrowed' || u.mineState === 'burrowing' || u.mineState === 'rising' || u.isMineRising) {
        continue;
      }

      if (!Number.isFinite(u.x)) {
        u.x = u.team === 'western' ? 100 : 1500;
      }
      if (!Number.isFinite(u.y)) {
        u.y = this.laneY + this.laneHeight / 2;
      }

      const rawCell = Math.floor(u.x / CELL_SIZE);
      const cellX = Number.isFinite(rawCell)
        ? Math.max(0, Math.min(numBuckets - 1, rawCell))
        : (u.team === 'western' ? 0 : numBuckets - 1);

      if (u.isAir) {
        this._airBuckets[cellX]?.push(u);
      } else {
        this._groundBuckets[cellX]?.push(u);
      }
    }

    this._processBucketCollisions(this._groundBuckets, numBuckets);
    this._processBucketCollisions(this._airBuckets, numBuckets);
  }

  update(dt, particles, audio) {
    if (this.isGameOver) return;

    this.tickCount++;
    this.stats?.update(dt);

    // Measure 8: Dynamic match tension calculation (dirty-checked to avoid per-tick audio volume/rate churn)
    if (audio?.setMatchTension) {
      const baseW = this.bases?.western;
      const baseE = this.bases?.eastern;
      if (baseW && baseE) {
        const maxW = baseW.maxHp || 1000;
        const maxE = baseE.maxHp || 1000;
        const minRatio = Math.min(baseW.hp / maxW, baseE.hp / maxE);
        // Base danger ramps up as either base drops below 50% HP
        const baseDanger = Math.max(0, Math.min(1, (0.50 - minRatio) / 0.50));
        let activeUnits = 0;
        const uList = this.units;
        const uLen = uList ? uList.length : 0;
        for (let i = 0; i < uLen; i++) {
          if (!uList[i].isDead) activeUnits++;
        }
        // Combat density ramps up to 12 active units on field
        const combatDensity = Math.max(0, Math.min(1, activeUnits / 12));
        const tension = Math.min(1, baseDanger * 0.7 + combatDensity * 0.3);
        if (this._lastMatchTension === undefined || Math.abs(this._lastMatchTension - tension) >= 0.01) {
          audio.setMatchTension(tension);
          this._lastMatchTension = tension;
        }
      }
    }

    // 0. Update deterministic in-flight simulation queues
    for (let i = this.pendingProjectiles.length - 1; i >= 0; i--) {
      const p = this.pendingProjectiles[i];
      p.ticksRemaining--;
      if (p.ticksRemaining <= 0) {
        this.pendingProjectiles.splice(i, 1);
        this._resolveProjectileImpact(p, particles, audio);
      }
    }

    for (let i = this.pendingCapsules.length - 1; i >= 0; i--) {
      const c = this.pendingCapsules[i];
      c.ticksRemaining--;
      if (c.ticksRemaining <= 0) {
        this.pendingCapsules.splice(i, 1);
        this._resolveCapsuleLanding(c, particles, audio);
      }
    }

    for (let i = this.pendingDrones.length - 1; i >= 0; i--) {
      const d = this.pendingDrones[i];

      // 1. Calculate direction vector towards the currently determined target waypoint
      const dx = d.targetX - d.x;
      const dy = d.targetY - d.y;
      const dist = Math.hypot(dx, dy);

      // Launch liftoff acceleration phase (first 20 ticks = 0.33s)
      let speedMult = 1.0;
      if (d.launchTicks !== undefined && d.launchTicks > 0) {
        d.launchTicks--;
        const launchP = 1.0 - (d.launchTicks / 20);
        speedMult = launchP * launchP;
      }
      const step = d.speedPx * speedMult * dt;

      // 2. Check impact: reached target waypoint (or touched living target enemy)
      // Contact detonation requires having cleared launch/arming phase
      const canImpact = (d.launchTicks === undefined || d.launchTicks <= 0);
      let reachedTarget = canImpact && (dist <= Math.max(step, 0.001));
      if (!reachedTarget && canImpact && d.targetEnemy && !d.targetEnemy.isDead && !d.targetEnemy.isParachuting && !d.targetEnemy.isCinematic && !d.targetEnemy.isUntargetable && !d.targetEnemy.isGoldenStatue) {
        const eDx = d.targetEnemy.x - d.x;
        const eDy = d.targetEnemy.y - d.y;
        const enemyDistSq = eDx * eDx + eDy * eDy;
        const hitRadius = (d.targetEnemy.radius || 14) + 2;
        if (enemyDistSq <= hitRadius * hitRadius) {
          reachedTarget = true;
          d.targetX = d.targetEnemy.x;
          d.targetY = d.targetEnemy.y;
        }
      }

      if (reachedTarget) {
        d.x = d.targetX;
        d.y = d.targetY;
        d.isDetonated = true;
        if (d.visualDrone) d.visualDrone.life = 0;
        this.pendingDrones.splice(i, 1);
        this._resolveDroneImpact(d, particles, audio);
        continue;
      }

      // 3. In-flight physical movement towards determined waypoint
      if (dist > 0.001 && step > 0) {
        d.x += (dx / dist) * step;
        d.y += (dy / dist) * step;
      }

      // 4. Once per second (every 60 ticks): re-determine target position or retarget if target died
      d.retrackTicks--;
      if (d.retrackTicks <= 0) {
        d.retrackTicks = 60; // Reset 1-second interval

        const enemyAlive = d.targetEnemy &&
          !d.targetEnemy.isDead &&
          !d.targetEnemy.isParachuting &&
          !d.targetEnemy.isCinematic &&
          !d.targetEnemy.isUntargetable &&
          !d.targetEnemy.isGoldenStatue &&
          (d.targetEnemy.isStructure || !d.targetEnemy.isCloaked || this.isPositionDetected(d.team, d.targetEnemy.x, d.targetEnemy.y));

        if (enemyAlive) {
          // Re-determine position of the selected enemy unit:
          d.targetX = d.targetEnemy.x;
          d.targetY = d.targetEnemy.y;
        } else {
          // Unit no longer exists / died: find closest enemy ground unit in range
          const enemyTeam = d.team === 'western' ? 'eastern' : 'western';
          let closestEnemy = null;
          let closestDistSq = Infinity;
          const rangePxSq = d.rangePx * d.rangePx;

          for (let uIdx = 0; uIdx < this.units.length; uIdx++) {
            const u = this.units[uIdx];
            if (u.team !== enemyTeam || u.isDead || u.isParachuting || u.isCinematic || u.isUntargetable || u.isGoldenStatue) continue;
            if (u.isAir) continue; // Ground units only
            if (u.isCloaked && !this.isPositionDetected(d.team, u.x, u.y)) continue;

            const dxD = u.x - d.x;
            const dyD = u.y - d.y;
            const distToDroneSq = dxD * dxD + dyD * dyD;

            const dxO = u.x - d.originX;
            const dyO = u.y - d.originY;
            const distToOriginSq = dxO * dxO + dyO * dyO;

            const inRange = (distToDroneSq <= rangePxSq) || (distToOriginSq <= rangePxSq);

            if (inRange && distToDroneSq < closestDistSq) {
              closestDistSq = distToDroneSq;
              closestEnemy = u;
            }
          }

          // Fallback: enemy base if within range and no units
          if (!closestEnemy && this.bases[enemyTeam]) {
            const base = this.bases[enemyTeam];
            const dxB = base.x - d.x;
            const dyB = base.y - d.y;
            const distToBaseSq = dxB * dxB + dyB * dyB;

            const dxOB = base.x - d.originX;
            const dyOB = base.y - d.originY;
            const distOriginToBaseSq = dxOB * dxOB + dyOB * dyOB;

            if (distToBaseSq <= rangePxSq || distOriginToBaseSq <= rangePxSq) {
              closestEnemy = base;
            }
          }

          if (closestEnemy) {
            d.targetEnemy = closestEnemy;
            d.targetX = closestEnemy.x;
            d.targetY = closestEnemy.y;
          } else {
            // No target in range: drone continues to last determined position
            d.targetEnemy = null;
          }
        }
      }
    }

    // Modules descending on their cable share the same tick-countdown
    // pattern as the queues above - see js/engine/modules.js.
    this.moduleSystem?.update(dt, particles, audio);

    // Visual Deploy Beacons
    if (this.deployBeacons && this.deployBeacons.length > 0) {
      for (let i = this.deployBeacons.length - 1; i >= 0; i--) {
        const b = this.deployBeacons[i];
        b.timer -= dt;
        if (b.timer <= 0) {
          this.deployBeacons.splice(i, 1);
        }
      }
    }

    // 1. Update all units
    // A unit's update() can remove OTHER units from this.units mid-loop - a
    // kamikaze splash that kills several Stickmen makes enforceDeadStickmanLimit()
    // crumble the oldest wrecks, which splice themselves out. Walking the live
    // array by index then reads past its end (TypeError) or removes the wrong
    // unit. So the pass runs over a snapshot, newest first as before; units
    // spawned during the pass are not in it, exactly as before. A unit removed
    // meanwhile is already isDead, and update() returns at once for those.
    const pass = this._updatePass || (this._updatePass = []);
    pass.length = 0;
    for (let i = 0; i < this.units.length; i++) pass.push(this.units[i]);
    for (let p = pass.length - 1; p >= 0; p--) {
      const u = pass[p];
      u.update(dt, this, particles, audio);
      if (u.isDead) {
        if (u.isHacker && u.hackerTargetId) {
          u._releaseHackerTarget?.(this, particles, audio);
        }
        const uIdx = this.units.lastIndexOf(u);
        if (uIdx !== -1) this.units.splice(uIdx, 1);
        this.unitMap.delete(u.id);
        if (u.isStructure && this.structures) {
          const sIdx = this.structures.indexOf(u);
          if (sIdx !== -1) this.structures.splice(sIdx, 1);
        }
        if (u.isGoldenStatue && this.goldenStatues) {
          const gIdx = this.goldenStatues.indexOf(u);
          if (gIdx !== -1) this.goldenStatues.splice(gIdx, 1);
        }
        if (u.isCocoon && this.cocoons) {
          const cIdx = this.cocoons.indexOf(u);
          if (cIdx !== -1) this.cocoons.splice(cIdx, 1);
        }
        if (u.isDetector && this.detectors?.[u.team]) {
          const dIdx = this.detectors[u.team].indexOf(u);
          if (dIdx !== -1) this.detectors[u.team].splice(dIdx, 1);
        }
      }
    }
    pass.length = 0;

    if (this.metamorphosisFogTimer > 0) {
      this.metamorphosisFogTimer = Math.max(0, this.metamorphosisFogTimer - dt);
    }

    // 2. Soft-body Collision & Flocking Separation (Gentle, preserves lane corridors)
    // Bucketed into a 1D spatial grid along X instead of comparing every unit
    // against every other unit - the lane is only 320px tall vs 1600px wide,
    // so X alone already does most of the culling work. Air and ground units
    // never collide with each other, so they get separate grids too.
    this.resolveCollisions();

    // Clamp within playable lane height with boundary margin
    for (const u1 of this.units) {
      if (u1.isDead || u1.isParachuting || u1.isCinematic) continue;
      if (u1.burrowAnchorX !== null && u1.burrowAnchorX !== undefined && Number.isFinite(u1.burrowAnchorX)
        && (u1.isBurrowed || u1.mineState === 'burrowed' || u1.mineState === 'burrowing' || u1.mineState === 'rising' || u1.isMineRising)) {
        u1.x = u1.burrowAnchorX;
        if (u1.burrowAnchorY !== null && u1.burrowAnchorY !== undefined && Number.isFinite(u1.burrowAnchorY)) {
          u1.y = u1.burrowAnchorY;
        }
        continue;
      }
      const margin = u1.radius + 10;
      const minY = this.laneY + margin;
      const maxY = this.laneY + this.laneHeight - margin;
      u1.y = Math.max(minY, Math.min(maxY, u1.y));
    }

    // 3. Update Territorial Dominance & Economy Bonus
    this.updateTerritorialControl(dt);

    // 3B. Update Nuclear Environmental Timer & Tree Regeneration
    if (this.isNuked) {
      this.nukeTimer += dt;
    }

    // 3C. Update Active Sul Precision Strikes & Retreat Protocol Signals
    this.updatePrecisionStrikes(dt, particles, audio);
    this.updateRetreatSignals(dt, particles, audio);

    // 4. Update Base Defense (Planetary Fortress Ibiks Cannon)
    this.updateBaseDefense(this.bases.western, 'eastern', dt, particles, audio);
    this.updateBaseDefense(this.bases.eastern, 'western', dt, particles, audio);

    // 5. Check Victory/Defeat
    if (this.bases.western.hp <= 0 && !this.isGameOver) {
      this.bases.western.hp = 0;
      this.isGameOver = true;
      this.winner = 'eastern';
      audio?.playBaseDestruction?.({ x: this.bases.western.x });
      audio?.setMatchTension?.(0);
    } else if (this.bases.eastern.hp <= 0 && !this.isGameOver) {
      this.bases.eastern.hp = 0;
      this.isGameOver = true;
      this.winner = 'western';
      audio?.playBaseDestruction?.({ x: this.bases.eastern.x });
      audio?.setMatchTension?.(0);
    }
  }

  updateBaseDefense(base, enemyTeam, dt, particles, audio) {
    if (base.attackCooldown > 0) {
      base.attackCooldown -= dt;
    }
    if (base.recoilTimer > 0) {
      base.recoilTimer -= dt;
    }

    // Single pass over all units instead of filter+filter+sort+sort per call
    // (this runs twice per frame, once per base). Priority 1: closest enemy
    // in range actively attacking this base. Priority 2: closest enemy in
    // range, period. Both tracked as running minimums in the same loop.
    let closestActive = null, closestActiveDstSq = Infinity;
    let closestAny = null, closestAnyDstSq = Infinity;
    const baseRange = base.range;
    const baseRangeSq = baseRange * baseRange;
    for (const eu of this.units) {
      if (eu.team !== enemyTeam || eu.isDead || eu.isParachuting || eu.isCinematic || eu.isUntargetable || eu.isGoldenStatue) continue;
      const dx = eu.x - base.x;
      if (dx > baseRange || dx < -baseRange) continue;
      const dy = eu.y - base.y;
      const dstSq = dx * dx + dy * dy;
      if (dstSq > baseRangeSq) continue;
      if (eu.isCloaked && !this.isPositionDetected(base.team, eu.x, eu.y)) continue;
      if (dstSq < closestAnyDstSq) { closestAny = eu; closestAnyDstSq = dstSq; }
      if (eu.target === base && dstSq < closestActiveDstSq && this.isInRange(eu, base)) {
        closestActive = eu; closestActiveDstSq = dstSq;
      }
    }
    const closest = closestActive || closestAny;

    base.targetEnemy = closest;

    // Smoothly rotate turret toward target enemy or default horizontal idle
    // angle. Measured from the turret's own ground emplacement, not from the
    // base centre - the two are no longer the same point since the turret
    // moved off the roof (see getBaseTurretX/Y).
    const turretX = this.getBaseTurretX(base.team);
    const turretY = this.getBaseTurretY(base.team);
    const defaultAngle = base.team === 'western' ? 0 : Math.PI;
    let targetAngle = defaultAngle;
    if (closest) {
      targetAngle = Math.atan2(closest.y - turretY, closest.x - turretX);
    }
    
    // Smooth angle interpolation - the diff must be wrapped to [-PI, PI]
    // (shortest rotational path) before lerping. Without this, the eastern
    // base's default/typical aim angle sits at +-PI (defaultAngle = PI,
    // enemies approach from the west so atan2 also lands near +-PI), so a
    // target flickering between just-above and just-below the turret's y
    // flips the raw angle between values near +PI and -PI. A naive lerp on
    // the raw difference then spins the turret the long way around through
    // 0 every time, instead of the short way through PI - it looks like the
    // cannon jams/locks facing the wrong way. The western base's default
    // angle (0) never sits near this wrap boundary, so it never showed the
    // bug.
    if (base.turretAngle === undefined) base.turretAngle = defaultAngle;
    let turretAngleDiff = targetAngle - base.turretAngle;
    turretAngleDiff = ((turretAngleDiff + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
    base.turretAngle += turretAngleDiff * 7.0 * dt;
    base.turretAngle = ((base.turretAngle + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;

    if (base.attackCooldown <= 0 && closest) {
      base.attackCooldown = base.attackSpeed;
      base.recoilTimer = 0.28; // Recoil kickback duration
      this.stats?.recordShot(base.team, true);

      const barrelLen = base.race === 'Sul' ? 6 : 32; // Sul fires directly from apex sphere (r~6px), Human has 32px barrel
      const bCos = Math.cos(base.turretAngle);
      const bSin = Math.sin(base.turretAngle);
      const muzzleX = turretX + bCos * barrelLen;
      const muzzleY = turretY + bSin * barrelLen;

      const targetImpactX = closest.x;
      const targetImpactY = closest.y;
      const dist = Math.hypot(targetImpactX - muzzleX, targetImpactY - muzzleY);
      const flightTimeSec = Math.max(0.20, Math.min(0.38, dist / 950));
      const flightTicks = Math.max(1, Math.round(flightTimeSec * 60));

      if (base.race === 'Sul') {
        // Sul Swarm Pyramidal Apex Energy Sphere Defense:
        // Multi-staged attack: precursor bio-electric vector lightning arc, followed by dense bio-plasma energy orb
        if (audio?.playBaseEnergy) audio.playBaseEnergy({ x: muzzleX });
        else audio?.playBark?.('Sul', 'attack', { x: muzzleX });

        particles?.addMuzzleFlash?.(muzzleX, muzzleY, '#d946ef', 22);
        particles?.addMuzzleFlash?.(muzzleX, muzzleY, '#fef08a', 14);
        particles?.addMuzzleFlash?.(muzzleX, muzzleY, '#ffffff', 8);
        particles?.addScreenShake?.(3.5, 0.15);

        particles?.addLightningArc?.(muzzleX, muzzleY, targetImpactX, targetImpactY);
        if (particles?.addSulEnergyOrb) {
          particles.addSulEnergyOrb(muzzleX, muzzleY, targetImpactX, targetImpactY);
        } else {
          particles?.addBallisticBaseShell?.(muzzleX, muzzleY, targetImpactX, targetImpactY, null, '#d946ef');
        }

        this.addProjectileSim({
          flightTicks,
          targetX: targetImpactX,
          targetY: targetImpactY,
          targetUnitId: closest.id,
          damage: base.damage,
          splashRadius: 55,
          team: base.team,
          projectileType: 'Plasma'
        });
      } else {
        // Human Planetary Fortress Ibiks Cannon
        if (audio?.playBaseCannon) audio.playBaseCannon({ x: muzzleX });
        else audio?.playExplosion?.('medium', { x: muzzleX });

        // Heavy Muzzle Flash & Screen Shake on firing
        particles?.addMuzzleFlash?.(muzzleX, muzzleY, '#ffaa00', 16);
        particles?.addMuzzleFlash?.(muzzleX, muzzleY, '#ffffff', 10);
        particles?.addScreenShake?.(3.5, 0.15);

        particles?.addBallisticBaseShell?.(muzzleX, muzzleY, targetImpactX, targetImpactY);
        this.addProjectileSim({
          flightTicks,
          targetX: targetImpactX,
          targetY: targetImpactY,
          targetUnitId: closest.id,
          damage: base.damage,
          splashRadius: 55,
          team: base.team,
          projectileType: 'Grenade'
        });
      }
    }
  }

  isInRange(attacker, target) {
    if (!attacker || !target) return false;
    // Range Ground/Air was unified into a single Range - see
    // NEUSTART_PLAN_DEFLECTION and Unit's constructor.
    let range = attacker.range || 24;
    if ((attacker.isStickman || attacker.cardData?.unitType === 'Stickman') && (target.isAir || target.elementType === 'Air')) {
      range *= 0.5;
    }

    // Bases and structures at the end of the lane have a wide defensive hit span across all vertical lanes
    if (target.isStructure) {
      const distX = Math.abs(attacker.x - target.x);
      const distY = Math.abs(attacker.y - target.y);
      const isBase = (target.name?.includes('Fortress') || target.name?.includes('Base'));
      const verticalHitSpan = isBase ? 180 : ((attacker.isHotHand ? (target.radius || 24) + 12 : (target.radius || 48)));
      if (attacker.isHotHand) {
        // Hot Hand delivers punches with molten bare fist: must close in tight to the structure boundary!
        const effectiveRadius = (target.radius || 38) + 16;
        if (distX <= (attacker.radius || 17) + effectiveRadius && distY <= verticalHitSpan) {
          return true;
        }
      } else {
        const effectiveRadius = (target.radius || 48) + 30;
        if (distX <= range + (attacker.radius || 20) + effectiveRadius && distY <= verticalHitSpan) {
          return true;
        }
      }
    }

    const dx = attacker.x - target.x;
    const dy = attacker.y - target.y;
    let maxDist = range + (attacker.radius || 20) + (target.radius || 12);
    if (attacker.isHotHand) {
      // Hot Hand: Bare-fist melee martial artist. Must close in right up to the enemy model so punches connect visually!
      maxDist = (attacker.radius || 17) + (target.radius || 12) + 8;
    }
    return (dx * dx + dy * dy) <= (maxDist * maxDist);
  }

  // Base-Defense & Corridor-Aware Intelligent Target Selection
  findBestTarget(unit, targetFilter = null) {
    const enemyTeam = unit.team === 'western' ? 'eastern' : 'western';
    const ownBase = this.bases[unit.team];
    const enemyBase = this.bases[enemyTeam];
    const dir = unit.team === 'western' ? 1 : -1;

    let bestActiveEnemyInRange = null;
    let minActiveEnemyScore = 999999;
    let bestCocoonAhead = null;
    let minCocoonScore = 999999;
    let bestGeneralEnemy = null;
    let minGeneralScore = 999999;

    const dxUnitBase = unit.x - ownBase.x;
    const dyUnitBase = unit.y - ownBase.y;
    const isNearHomeBase = (dxUnitBase * dxUnitBase + dyUnitBase * dyUnitBase) <= 250000;

    // Iterates this.units directly instead of building a filtered array
    // first (findBestTarget runs every time a unit needs a new target, so a
    // fresh array on every call adds up) - same filter conditions as
    // before, same iteration order, just no intermediate allocation.
    // Targets Air/Targets Ground was removed as a mechanic - every unit
    // attacks all Element Types now (see Marine's card description and
    // Unit.resolveNewCombatAttack()), so no isAir-based filtering here
    // anymore beyond the per-unit opt-out below.
    for (const enemy of this.units) {
      if (enemy.isDead || enemy.isParachuting || enemy.isCinematic || enemy.isGoldenStatue) continue;
      if (enemy.isUntargetable || (enemy.executionTimer && enemy.executionTimer > 0)) continue;

      // 1. Sul Metamorphosis Cocoons: Attackable by any unit, friend-or-foe agnostic
      if (enemy.isCocoon) {
        if (targetFilter === 'Air') continue; // Cocoons are grounded
        // Prioritize in march direction between unit and enemy base
        const forwardDist = (enemy.x - unit.x) * dir;
        const distToBase = enemyBase ? (enemyBase.x - enemy.x) * dir : 999;
        if (forwardDist >= -unit.radius && distToBase >= -10) {
          const lateralDist = Math.abs(enemy.y - unit.y);
          const cocoonScore = Math.max(0, forwardDist) + lateralDist * 1.5;
          if (cocoonScore < minCocoonScore) {
            minCocoonScore = cocoonScore;
            bestCocoonAhead = enemy;
          }
        }
        continue;
      }

      if (enemy.team !== enemyTeam) continue;
      if (unit.cannotAttackBuildings && enemy.isStructure) continue;
      if (enemy.isCloaked && !this.isPositionDetected(unit.team, enemy.x, enemy.y)) {
        // Anti Shade duel: Anti Shades perceive and duel each other across the veil without decloaking to other units
        if (!(unit.isAntiShade && enemy.isAntiShade)) {
          continue;
        }
      }
      // Target filter: 'Ground' only targets ground units; 'Air' only targets air units
      const enemyIsAir = !!(enemy.isAir || enemy.elementType === 'Air');
      if (targetFilter === 'Ground' && enemyIsAir) continue;
      if (targetFilter === 'Air' && !enemyIsAir) continue;
      // Per-unit opt-out (e.g. Panzer & Orchestra: No Air) - see Unit constructor.
      if (enemyIsAir && unit.targetsAir === false) continue;
      if (!enemyIsAir && unit.targetsGround === false) continue;

      // Anti Shade: Frontline Sentinel. Can cross the bonus indicator line up to a generous
      // distance to engage enemies, but never pursues deep towards the enemy base or retreats
      // across the field, keeping him anchored to the frontline bonus indicator.
      if (unit.isAntiShade) {
        const frontlineX = this.territoryDivider !== undefined ? this.territoryDivider : (this.width / 2);
        const dir = unit.team === 'western' ? 1 : -1;
        const enemyOvershoot = (enemy.x - frontlineX) * dir;
        const dxToFront = Math.abs(enemy.x - frontlineX);
        const dxToUnit = Math.abs(enemy.x - unit.x);
        const dyToUnit = Math.abs(enemy.y - unit.y);

        // Anti Shade is allowed to cross the bonus indicator line up to 260px into enemy territory,
        // and defends within 260px on the friendly side, but ignores enemies beyond that leash.
        const maxCrossingDist = 260;
        const isShadowDuel = unit.isAntiShade && enemy.isAntiShade;
        const exceedsEnemyTerritoryLeash = !isShadowDuel && enemyOvershoot > maxCrossingDist;
        if (exceedsEnemyTerritoryLeash) {
          continue;
        }
        const inFrontlineZone = (
          (dxToFront <= maxCrossingDist && dyToUnit <= 180) ||
          (dxToUnit <= 100 && dyToUnit <= 160) ||
          (isShadowDuel && dyToUnit <= 180)
        );
        if (!inFrontlineZone) {
          continue;
        }
      }

      const forwardDist = (enemy.x - unit.x) * dir; // Distance ahead of us
      const lateralDist = Math.abs(enemy.y - unit.y); // Distance across lanes
      const dxEnemyBase = enemy.x - ownBase.x;
      const dyEnemyBase = enemy.y - ownBase.y;
      const isAttackingHomeBase = (dxEnemyBase * dxEnemyBase + dyEnemyBase * dyEnemyBase) <= 176400;

      const dxEnemy = enemy.x - unit.x;
      const dyEnemy = enemy.y - unit.y;
      const distSq = dxEnemy * dxEnemy + dyEnemy * dyEnemy;

      let score = 0;

      if (isAttackingHomeBase && isNearHomeBase) {
        // TOP EMERGENCY PRIORITY: Enemy is attacking/threatening our home base!
        // All defenders in home half of field immediately prioritize killing this threat!
        score = -10000 + distSq * 0.001;
      } else if (isAttackingHomeBase) {
        // Threat at home base, but unit is further up the field
        score = -5000 + distSq * 0.001;
      } else {
        // Standard frontline corridor combat
        if (unit.isAntiShade) {
          const frontlineX = this.territoryDivider !== undefined ? this.territoryDivider : (this.width / 2);
          const dxToFront = Math.abs(enemy.x - frontlineX);
          const distToUnit = Math.sqrt(distSq);
          score = distToUnit + dxToFront * 0.4;
        } else {
          const forwardWeight = forwardDist < -15 ? 600 + Math.abs(forwardDist) * 3 : Math.max(0, forwardDist);
          const laneWeight = lateralDist * 3.5;
          score = forwardWeight + laneWeight;
        }
      }

      // Changeling Disguise: once it's copied an identity, it draws fire
      // preferentially - "absorbs incoming first strikes" - though an actual
      // base-defense emergency (scored far below -3000 above) still wins.
      if (enemy.isDecoy && enemy.disguiseUnitType) {
        score -= 3000;
      }

      // Ground target prioritization (e.g. Processing Pillar):
      // Air targets receive a score penalty so any in-range ground enemy is targeted first
      if (enemy.isAir && unit.prioritizeGround) {
        score += 10000;
      }

      // Check if this active enemy is threatening home base OR currently in attack range / scout range
      const inAttackRange = this.isInRange(unit, enemy);

      // Sul Retreat Protocol: Rear-guard Bastion Locks taunt & draw priority
      if (enemy.isBastionRearGuard || enemy.isPriorityTarget) {
        if (inAttackRange) {
          score -= 9000; // Immediate top priority within weapon range
        } else {
          score -= 3500; // High draw weight across the field
        }
      }

      // Retreating units: lower threat priority so pursuing units switch to rear-guards
      if (enemy.isRetreating) {
        score += 2000;
      }
      const scoutRange = unit.scoutRange || (unit.isHotHand ? 288 : (unit.isKamikaze ? 288 : 0));
      const scoutLateralRange = unit.scoutLateralRange || (unit.isKamikaze ? 156 : 0);
      const inLateralScope = !scoutLateralRange || lateralDist <= (scoutLateralRange + (unit.radius || 20) + (enemy.radius || 12));
      const inForwardScope = !unit.isKamikaze || forwardDist >= -15;

      const isBaseDefenseTarget = isAttackingHomeBase && (!unit.isKamikaze || inLateralScope);
      const inScoutRange = scoutRange > 0 && inLateralScope && inForwardScope && distSq <= Math.pow(scoutRange + (unit.radius || 20) + (enemy.radius || 12), 2);
      if (isBaseDefenseTarget || inAttackRange || inScoutRange) {
        if (score < minActiveEnemyScore) {
          minActiveEnemyScore = score;
          bestActiveEnemyInRange = enemy;
        }
      }

      if (score < minGeneralScore) {
        minGeneralScore = score;
        bestGeneralEnemy = enemy;
      }
    }

    // 1. Top priority: Active attacking enemy units in attack range (or threatening home base)
    if (bestActiveEnemyInRange) {
      return bestActiveEnemyInRange;
    }

    // 2. Next priority: Nearest cocoon in march direction between unit and enemy base
    if (bestCocoonAhead) {
      return bestCocoonAhead;
    }

    // 3. Priority: If no cocoons are between unit and base, prioritize enemy base attack
    const canAttackBase = enemyBase && !enemyBase.isDead && !(unit.isParasiteVulture && unit.hasParasite) && !unit.cannotAttackBuildings && targetFilter !== 'Air';
    if (canAttackBase) {
      return enemyBase;
    }

    // 4. Fallback: general best enemy if unit cannot attack buildings
    if (bestGeneralEnemy) {
      return bestGeneralEnemy;
    }

    if ((unit.isParasiteVulture && unit.hasParasite) || unit.cannotAttackBuildings || targetFilter === 'Air' || targetFilter === 'Ground') {
      return null;
    }

    return enemyBase;
  }

  // Coarse "does this team have any detection capability at all" flag - used
  // by things that just need a yes/no (AI decisions, Intel Uplink's own
  // reveal). Actual visibility of a specific spot goes through
  // isPositionDetected() below, which is range-limited to each detector.
  hasTeamDetection(team) {
    if (this.buildingSystem?.has(team, 'intel_uplink')) return true;
    const list = this.detectors?.[team];
    if (list && list.length > 0) {
      for (let i = 0; i < list.length; i++) {
        const u = list[i];
        if (!u.isDead && !u.isGoldenStatue && !u.isCocoon) return true;
      }
      return false;
    }
    for (let i = 0; i < this.units.length; i++) {
      const u = this.units[i];
      if (u.team === team && u.isDetector && !u.isDead && !u.isGoldenStatue && !u.isCocoon) {
        if (this.detectors?.[team]) this.detectors[team].push(u);
        return true;
      }
    }
    return false;
  }

  // Positional detection: true only if (x, y) falls within an actual living
  // detector's own detectionRange, a globalDetector unit (e.g. A9 Grizzly) is
  // alive on the map, or Intel Uplink's map-wide sensor network is active.
  isPositionDetected(team, x, y) {
    if (this.buildingSystem?.has(team, 'intel_uplink')) return true;
    const list = this.detectors?.[team];
    if (list && list.length > 0) {
      for (let i = 0; i < list.length; i++) {
        const u = list[i];
        if (u.isDead || u.isGoldenStatue || u.isCocoon) continue;
        if (u.globalDetector || u.cardData?.globalDetector) return true;
        const dx = u.x - x;
        const dy = u.y - y;
        const detRange = u.detectionRange;
        if ((dx * dx + dy * dy) <= detRange * detRange) return true;
      }
      return false;
    }
    // Fallback for direct unit instantiations in tests without spawnRaw
    for (let i = 0; i < this.units.length; i++) {
      const u = this.units[i];
      if (u.team === team && u.isDetector && !u.isDead && !u.isGoldenStatue && !u.isCocoon) {
        if (this.detectors?.[team]) this.detectors[team].push(u);
        if (u.globalDetector || u.cardData?.globalDetector) return true;
        const dx = u.x - x;
        const dy = u.y - y;
        const detRange = u.detectionRange;
        if ((dx * dx + dy * dy) <= detRange * detRange) return true;
      }
    }
    return false;
  }

  findWoundedAlly(healer) {
    let best = null;
    let minHpRatio = 1.0;
    const maxRange = healer.range * 1.5;
    const maxRangeSq = maxRange * maxRange;
    const units = this.units;
    const len = units.length;

    for (let i = 0; i < len; i++) {
      const u = units[i];
      if (u.team !== healer.team || u.isDead || u === healer || u.hp >= u.maxHp || !u.attributes?.includes('Biological')) {
        continue;
      }
      const dx = u.x - healer.x;
      const dy = u.y - healer.y;
      const dstSq = dx * dx + dy * dy;
      if (dstSq <= maxRangeSq) {
        const ratio = u.hp / u.maxHp;
        if (ratio < minHpRatio) {
          minHpRatio = ratio;
          best = u;
        }
      }
    }
    return best;
  }

  // hitAir/hitGround are accepted for existing call-site compatibility but
  // no longer filter anything - Targets Air/Targets Ground was removed as
  // a mechanic (every unit "attacks all Element Types" now, see
  // Unit.resolveNewCombatAttack()). projectileType is forwarded to each
  // victim's takeDamage() so Deflection Rating still rolls per splashed
  // unit (Energy-type splash - unlikely, but possible - always bypasses it).
  dealSplashDamage(centerX, centerY, radius, damage, sourceTeam, hitAir, hitGround, particles, projectileType = 'Bullet', directTarget = null, friendlyFire = false, attacker = null, hasIncinerate = false, incinerateDuration = 5, incinerateDps = 2, coreRadius = 0, canHitBase = true) {
    const enemyTeam = sourceTeam === 'western' ? 'eastern' : 'western';
    const isGold = !!(attacker?.edition === 'gold');
    for (const u of this.units) {
      if (u.isDead || u.isParachuting || u.isCinematic) continue;
      if (directTarget && u === directTarget) continue;
      if (attacker && u === attacker) continue;

      const isTargetTeam = (u.team === enemyTeam) || (friendlyFire && u.team === sourceTeam) || u.isCocoon;
      if (isTargetTeam && ((u.isAir && hitAir) || (!u.isAir && hitGround))) {
        const dx = u.x - centerX;
        const dy = u.y - centerY;
        const maxR = radius + u.radius;
        const distSq = dx * dx + dy * dy;
        if (distSq <= maxR * maxR) {
          const dst = Math.sqrt(distSq);
          const falloff = (coreRadius > 0 && dst <= coreRadius)
            ? 1.0
            : (1 - (Math.max(0, dst - coreRadius) / Math.max(1, maxR - coreRadius)) * 0.4);
          u.takeDamage(damage * Math.max(0.4, falloff), particles, projectileType, true, isGold, attacker);
          if (hasIncinerate && typeof u.applyIncinerate === 'function') {
            u.applyIncinerate(incinerateDuration, incinerateDps, sourceTeam);
          }
        }
      }
    }

    const enemyBase = this.bases[enemyTeam];
    if (canHitBase && enemyBase && (!directTarget || directTarget !== enemyBase)) {
      const dxBase = enemyBase.x - centerX;
      const dyBase = enemyBase.y - centerY;
      const maxRBase = radius + enemyBase.radius;
      const distSqBase = dxBase * dxBase + dyBase * dyBase;
      if (distSqBase <= maxRBase * maxRBase) {
        enemyBase.takeDamage(damage * 0.8, particles, projectileType, true, isGold, attacker);
        if (hasIncinerate && typeof enemyBase.applyIncinerate === 'function') {
          enemyBase.applyIncinerate(incinerateDuration, incinerateDps, sourceTeam);
        }
      }
    }
  }

  dealLineSplashDamage(startX, startY, endX, endY, width, damage, sourceTeam, hitAir, hitGround, particles, attacker = null) {
    const enemyTeam = sourceTeam === 'western' ? 'eastern' : 'western';
    const isGold = !!(attacker?.edition === 'gold');
    const dx = endX - startX;
    const dy = endY - startY;
    const lenSq = dx * dx + dy * dy;

    for (const u of this.units) {
      if ((u.team === enemyTeam || u.isCocoon) && !u.isDead) {
        if ((u.isAir && hitAir) || (!u.isAir && hitGround)) {
          let t = 0;
          if (lenSq > 0) {
            t = Math.max(0, Math.min(1, ((u.x - startX) * dx + (u.y - startY) * dy) / lenSq));
          }
          const projX = startX + t * dx;
          const projY = startY + t * dy;
          const diffX = u.x - projX;
          const diffY = u.y - projY;
          const maxR = width + u.radius;
          if ((diffX * diffX + diffY * diffY) <= maxR * maxR) {
            u.takeDamage(damage, particles, 'Bullet', true, isGold, attacker);
          }
        }
      }
    }

    const enemyBase = this.bases[enemyTeam];
    if (hitGround && enemyBase) {
      let t = 0;
      if (lenSq > 0) {
        t = Math.max(0, Math.min(1, ((enemyBase.x - startX) * dx + (enemyBase.y - startY) * dy) / lenSq));
      }
      const projX = startX + t * dx;
      const projY = startY + t * dy;
      const diffBaseX = enemyBase.x - projX;
      const diffBaseY = enemyBase.y - projY;
      const maxRBase = width + enemyBase.radius;
      if ((diffBaseX * diffBaseX + diffBaseY * diffBaseY) <= maxRBase * maxRBase) {
        enemyBase.takeDamage(damage * 0.8, particles, 'Bullet', true, isGold, attacker);
      }
    }
  }

  dealAreaDamage(centerX, centerY, radius, damage, sourceTeam, particles) {
    const enemyTeam = sourceTeam === 'western' ? 'eastern' : 'western';
    for (const u of this.units) {
      if ((u.team === enemyTeam || u.isCocoon) && !u.isDead) {
        const dx = u.x - centerX;
        const dy = u.y - centerY;
        const maxR = radius + u.radius;
        if ((dx * dx + dy * dy) <= maxR * maxR) {
          u.takeDamage(damage, particles);
        }
      }
    }

    const enemyBase = this.bases[enemyTeam];
    const dxBase = enemyBase.x - centerX;
    const dyBase = enemyBase.y - centerY;
    const maxRBase = radius + enemyBase.radius;
    if ((dxBase * dxBase + dyBase * dyBase) <= maxRBase * maxRBase) {
      enemyBase.takeDamage(damage, particles);
    }
  }

  handleUnitDeath(dyingUnit, particles, killer = null) {
    if (!dyingUnit || !this.units || dyingUnit.isCocoon) return;
    if (dyingUnit.isHacker) {
      dyingUnit._releaseHackerTarget?.(this, particles);
    }
    const unitCost = dyingUnit.cardData?.cost || 0;
    if (unitCost <= 0) return;

    // Sul Looting Vulture: whenever an enemy unit gets killed by looting vulture, receive 50% of the killed unit's plasma cost
    if (killer && (killer.name === 'Looting Vulture' || killer.cardData?.unitType === 'LootingVulture' || killer.isLootingVulture)) {
      const lootAmount = Math.round(unitCost * (killer.bountyRatio !== undefined ? killer.bountyRatio : 0.5));
      if (lootAmount > 0) {
        if (this.economy) {
          this.economy.addPlasma(killer.team, lootAmount);
        }
        this.stats?.recordSalvagePlasma(killer.team, lootAmount);
        if (particles) {
          particles.addDamageText?.(killer.x, killer.y - (killer.radius || 34) - 8, `+${lootAmount} PLASMA`, 'gold');
          particles.addHitSparks?.(killer.x, killer.y, 'gold', 6);
          particles.addGoldDust?.(dyingUnit.x, dyingUnit.y, 8);
        }
      }
    }

    const salvageAmount = Math.ceil(unitCost * 0.1);
    if (salvageAmount <= 0) return;

    for (let i = 0; i < this.units.length; i++) {
      const u = this.units[i];
      if (u === dyingUnit || u.isDead) continue;
      if (u.name === 'The Salvage Company' || u.cardData?.unitType === 'TheSalvageCompany') {
        if (this.economy) {
          this.economy.addPlasma(u.team, salvageAmount);
        }
        this.stats?.recordSalvagePlasma(u.team, salvageAmount);
        if (particles) {
          particles.addDamageText?.(u.x, u.y - (u.radius || 28) - 8, `+${salvageAmount} GAS`, 'heal');
          particles.addHitSparks?.(u.x, u.y, 'metal', 3);
        }
      }
    }
  }

  // Ensures at most `maxCount` (default 3) dead Stickman obstacles remain on the map;
  // when a 4th Stickman dies, the oldest dead Stickman on the field crumbles and is removed.
  enforceDeadStickmanLimit(particles = null, maxCount = 3) {
    if (!this.goldenStatues) return;
    const deadStickmen = this.goldenStatues.filter(
      s => s && (s.isStickman || s.cardData?.unitType === 'Stickman' || s.name === 'Stickman') && s.isGoldenStatue && !s.hasGoldPedestal && !s.isDead
    );
    while (deadStickmen.length > maxCount) {
      const oldest = deadStickmen.shift();
      if (oldest) {
        if (typeof oldest.crumbleObstacle === 'function') {
          oldest.crumbleObstacle(particles || this.particles, this);
        } else {
          oldest.isDead = true;
          oldest.isGoldenStatue = false;
          oldest.hp = 0;
          const gIdx = this.goldenStatues.indexOf(oldest);
          if (gIdx !== -1) this.goldenStatues.splice(gIdx, 1);
          this.unitMap?.delete(oldest.id);
          const uIdx = this.units.indexOf(oldest);
          if (uIdx !== -1) this.units.splice(uIdx, 1);
        }
      }
    }
  }

  sweepSuperweapon(particles, superType = 'NUKE') {
    const units = [...(this.units || [])];
    for (const u of units) {
      u.takeDamage(99999, particles, 'Energy', false, false, null, false, { isSuperweapon: true, superType });
    }
    if (this.goldenStatues) {
      const statues = [...this.goldenStatues];
      for (const s of statues) {
        if (s.isStickman) {
          s.vaporizeObstacle?.(particles, superType);
        }
      }
    }
  }

  healAllFriendly(team, amount) {
    for (const u of this.units) {
      if (u.team === team && !u.isDead) {
        u.hp = Math.min(u.maxHp, u.hp + amount);
        if (u.maxShields > 0) {
          u.shields = Math.min(u.maxShields, u.shields + amount * 0.5);
        }
      }
    }
    const base = this.bases[team];
    base.hp = Math.min(base.maxHp, base.hp + amount * 0.5);
  }

  applyBuffAll(team, buff) {
    for (const u of this.units) {
      if (u.team === team && !u.isDead) {
        u.buffs.push({ ...buff });
      }
    }
  }

  triggerMetamorphosisFogEvent(team) {
    this.metamorphosisFogTimer = 4.5;
    this.metamorphosisFogTeam = team;
    this.stats?.recordMetamorphosisCast?.(team);
    if (this.particles?.addScreenShake) {
      this.particles.addScreenShake(4, 0.35);
    }
    this.addFrameEvent?.({ type: 'EVENT', name: 'experimentalMetamorphosis', team });
  }

  isFogEventActive() {
    if (this.metamorphosisFogTimer > 0) return true;
    if (this.mapId === 'swamp') {
      const duration = this.stats?.duration ?? 0;
      const intensity = getSwampFogIntensity(duration, this.mapSeed ?? 0);
      return intensity >= 0.15;
    }
    return false;
  }

  // --------------------------------------------------------------------------
  // SUL PRECISION STRIKE (Tier 1 Common Spell)
  // --------------------------------------------------------------------------
  triggerPrecisionStrike(team) {
    if (!this.activePrecisionStrikes) this.activePrecisionStrikes = [];
    const enemyTeam = team === 'western' ? 'eastern' : 'western';
    const startX = team === 'western' ? -220 : this.width + 220;
    const dir = team === 'western' ? 1 : -1;
    const target = this._findPrecisionStrikeTarget(team);

    this.activePrecisionStrikes.push({
      id: 'precision_strike_' + (this._strikeCounter = (this._strikeCounter || 0) + 1),
      team,
      enemyTeam,
      state: target ? 'APPROACHING' : 'SEARCHING',
      targetUnit: target || null,
      targetUnitId: target ? target.id : null,
      targetX: target ? target.x : 0,
      targetY: target ? target.y : 0,
      shadowX: startX,
      shadowDir: dir,
      shadowSpeed: 680,
      shadowY: this.laneY + this.laneHeight * 0.5,
      isBombSpawned: false,
      bombAltitude: 380,
      bombDuration: 0.65,
      flybyPlayed: false,
      isResolved: false
    });

    this.addFrameEvent?.({ type: 'EVENT', name: 'precisionStrike', team });
  }

  _findPrecisionStrikeTarget(team) {
    const enemyTeam = team === 'western' ? 'eastern' : 'western';
    const dividerX = (this.territoryDivider !== undefined && this.territoryDivider !== null)
      ? this.territoryDivider
      : (this.width / 2);

    let bestUnit = null;
    let minDistance = Infinity;

    for (let i = 0; i < this.units.length; i++) {
      const u = this.units[i];
      if (u.team !== enemyTeam || u.isDead) continue;
      // Can bomb structures and units, but NOT bases (nor decorative/transient entities)
      if (u.isBase || u.isCocoon || u.isGoldenStatue || u.isCinematic || u.isParachuting) continue;
      // Cannot target air or cloaked units
      if (u.isAir || u.elementType === 'Air' || u.cardData?.elementType === 'Air' || u.cardData?.isAir) continue;
      if (u.isCloaked || u.isAntiShade || u.cardData?.isCloaked) continue;
      if (u.isHotHand && !u.firstHitDone) continue;

      // Option 1: Horizontal distance to bonus indicator (territoryDivider)
      const dist = Math.abs(u.x - dividerX);
      if (dist < minDistance) {
        minDistance = dist;
        bestUnit = u;
      }
    }
    return bestUnit;
  }

  updatePrecisionStrikes(dt, particles, audio) {
    if (!this.activePrecisionStrikes || this.activePrecisionStrikes.length === 0) return;

    for (let i = this.activePrecisionStrikes.length - 1; i >= 0; i--) {
      const strike = this.activePrecisionStrikes[i];

      // 1. Advance shadow position
      const prevShadowX = strike.shadowX;
      strike.shadowX += strike.shadowDir * strike.shadowSpeed * dt;

      // Map boundary turnaround (applies before bomb is dropped: SEARCHING or APPROACHING)
      if (strike.state === 'SEARCHING' || strike.state === 'APPROACHING') {
        if (strike.shadowDir > 0 && strike.shadowX > this.width + 250) {
          strike.shadowDir = -1;
          strike.shadowX = this.width + 250;
          strike.flybyPlayed = false;
        } else if (strike.shadowDir < 0 && strike.shadowX < -250) {
          strike.shadowDir = 1;
          strike.shadowX = -250;
          strike.flybyPlayed = false;
        }
      }

      // 2. Lifecycle States
      if (strike.state === 'SEARCHING') {
        const target = this._findPrecisionStrikeTarget(strike.team);
        if (target) {
          strike.targetUnit = target;
          strike.targetUnitId = target.id;
          strike.state = 'APPROACHING';
        }
      }

      if (strike.state === 'APPROACHING') {
        if (!strike.flybyPlayed) {
          strike.flybyPlayed = true;
          audio?.playPrecisionStrikeFlyby?.({ x: strike.shadowX });
        }

        // Target verification before bomb spawn:
        // If target dies before bomb spawns, shadow flies off into the void and loops back searching
        if (!strike.targetUnit || strike.targetUnit.isDead) {
          strike.targetUnit = null;
          strike.targetUnitId = null;
          strike.state = 'SEARCHING';
          continue;
        }

        // Continuously track target coordinates
        strike.targetX = strike.targetUnit.x;
        strike.targetY = strike.targetUnit.y;

        // Drop bomb ONLY when shadow actually reaches/crosses target's horizontal position
        const crossedEast = strike.shadowDir > 0 && prevShadowX <= strike.targetUnit.x && strike.shadowX >= strike.targetUnit.x;
        const crossedWest = strike.shadowDir < 0 && prevShadowX >= strike.targetUnit.x && strike.shadowX <= strike.targetUnit.x;
        const reachedTarget = crossedEast || crossedWest;

        if (reachedTarget) {
          strike.isBombSpawned = true;
          strike.state = 'BOMB_FALLING';
          strike.bombAltitude = 380;
          audio?.playPrecisionStrikeDrop?.({ x: strike.targetX });
        }
      }

      if (strike.state === 'BOMB_FALLING') {
        // Accelerate bomb downward to earth
        const fallSpeed = 380 / strike.bombDuration;
        strike.bombAltitude -= fallSpeed * dt;

        // Trailing toxic green spores emitter
        if (particles && particles.canSpawnCosmetic?.()) {
          const trailY = strike.targetY - strike.bombAltitude;
          const sporeColors = ['#a3e635', '#84cc16', '#bef264', '#65a30d'];
          for (let p = 0; p < 2; p++) {
            particles.pushParticle({
              x: strike.targetX + (this.rng ? this.rng.range(-5, 5) : (Math.random() * 10 - 5)),
              y: trailY + (this.rng ? this.rng.range(-5, 5) : (Math.random() * 10 - 5)),
              vx: (this.rng ? this.rng.range(-15, 15) : (Math.random() * 30 - 15)),
              vy: -20 - (this.rng ? this.rng.range(5, 25) : (Math.random() * 20)),
              radius: 2 + (this.rng ? this.rng.next() * 2.5 : Math.random() * 2.5),
              color: sporeColors[Math.floor(Math.random() * sporeColors.length)],
              life: 0.35 + Math.random() * 0.25,
              maxLife: 0.6,
              alpha: 0.85
            });
          }
        }

        // Bomb impacts ground! (Even if target unit died during bomb flight)
        if (strike.bombAltitude <= 0) {
          strike.bombAltitude = 0;
          strike.isResolved = true;
          strike.state = 'RESOLVED';

          const hitX = strike.targetX;
          const hitY = strike.targetY;

          // 1. Direct damage (70 damage, Energy bypasses Deflection Rating)
          const directTarget = strike.targetUnit && !strike.targetUnit.isDead ? strike.targetUnit : null;
          if (directTarget) {
            directTarget.takeDamage(70, particles, 'Energy', true, false, null);
          }

          // 2. Heavy Splash Damage (70 damage, 72px radius, core radius 45px full damage, hits all units & structures, ignores Deflection, does NOT damage bases)
          this.dealSplashDamage(hitX, hitY, 72, 70, strike.team, true, true, particles, 'Energy', directTarget, false, null, false, 5, 2, 45, false);

          // 3. Audio & Shatter visuals
          audio?.playPrecisionStrikeImpact?.({ x: hitX });

          if (particles) {
            particles.addShockwave?.(hitX, hitY, 95, '#d946ef', 0.4);
            particles.addShockwave?.(hitX, hitY, 65, '#84cc16', 0.28);
            particles.addScorchMark?.(hitX, hitY, 24);
            particles.addScreenShake?.(8, 0.35);

            // Shattering particles: violet/purple chitin shards & toxic green spores
            const shatterColors = ['#c084fc', '#d946ef', '#a855f7', '#7e22ce', '#e879f9', '#ffffff'];
            for (let s = 0; s < 32; s++) {
              const angle = Math.random() * Math.PI * 2;
              const spd = 60 + Math.random() * 220;
              particles.pushParticle({
                x: hitX,
                y: hitY,
                vx: Math.cos(angle) * spd,
                vy: Math.sin(angle) * spd * 0.55 - 25,
                radius: 2.5 + Math.random() * 3.5,
                color: shatterColors[Math.floor(Math.random() * shatterColors.length)],
                life: 0.4 + Math.random() * 0.5,
                maxLife: 0.9,
                alpha: 0.95
              });
            }
            for (let g = 0; g < 20; g++) {
              const angle = Math.random() * Math.PI * 2;
              const spd = 30 + Math.random() * 140;
              particles.pushParticle({
                x: hitX,
                y: hitY,
                vx: Math.cos(angle) * spd,
                vy: Math.sin(angle) * spd * 0.5 - 40,
                radius: 2.0 + Math.random() * 2.5,
                color: Math.random() > 0.4 ? '#84cc16' : '#a3e635',
                life: 0.5 + Math.random() * 0.6,
                maxLife: 1.1,
                alpha: 0.9
              });
            }
          }
        }
      }

      // Cleanup when strike is resolved and the overflight shadow has cleared the screen
      if (strike.isResolved) {
        const shadowOffscreen = (strike.shadowDir > 0 && strike.shadowX > this.width + 250) ||
                                (strike.shadowDir < 0 && strike.shadowX < -250);
        if (shadowOffscreen) {
          this.activePrecisionStrikes.splice(i, 1);
        }
      }
    }
  }

  // --------------------------------------------------------------------------
  // SUL RETREAT PROTOCOL (Tier 2 Epic Spell)
  // --------------------------------------------------------------------------
  triggerRetreatProtocol(team) {
    if (!this.activeRetreatSignals) this.activeRetreatSignals = [];
    const ownBase = this.bases?.[team];
    const enemyTeam = team === 'western' ? 'eastern' : 'western';
    const enemyBase = this.bases?.[enemyTeam];
    const originX = ownBase ? ownBase.x : (team === 'western' ? 100 : this.width - 100);
    const targetX = enemyBase ? enemyBase.x : (team === 'western' ? this.width - 100 : 100);
    const dir = team === 'western' ? 1 : -1;
    const distance = Math.abs(targetX - originX);
    const duration = 4.0;
    const speed = distance / duration;

    const signal = {
      id: 'retreat_signal_' + (this._retreatCounter = (this._retreatCounter || 0) + 1),
      team,
      originX,
      targetX,
      dir,
      speed,
      duration,
      timer: 0,
      currentWaveX: originX,
      bastionsSpawned: false
    };

    this.activeRetreatSignals.push(signal);

    const effAudio = this.audio || (typeof window !== 'undefined' ? window.tcgGame?.audio : null);
    effAudio?.playRetreatProtocolSignal?.({ x: originX });

    this.addFrameEvent?.({
      type: 'EVENT',
      name: 'retreatProtocol',
      team,
      originX: Math.round(originX),
      speed: Math.round(speed)
    });
  }

  updateRetreatSignals(dt, particles, audio) {
    if (!this.activeRetreatSignals || this.activeRetreatSignals.length === 0) return;

    for (let i = this.activeRetreatSignals.length - 1; i >= 0; i--) {
      const sig = this.activeRetreatSignals[i];
      sig.timer += dt;
      sig.currentWaveX = sig.originX + sig.dir * sig.speed * Math.min(sig.duration, sig.timer);

      // 1. Tag any friendly mobile unit reached by the wave front
      if (this.units) {
        for (let u = 0; u < this.units.length; u++) {
          const unit = this.units[u];
          if (unit.team !== sig.team || unit.isDead) continue;
          if (unit.isStructure || unit.isBuilding || unit.isBase || unit.isGoldenStatue || unit.isBastionRearGuard) continue;
          if (unit.isRetreating) continue;

          // Has wavefront passed the unit's X coordinate?
          const reached = (unit.x - sig.originX) * sig.dir <= (sig.currentWaveX - sig.originX) * sig.dir;
          if (reached) {
            unit.startRetreat(this.bases?.[sig.team]);
            particles?.addShockwave?.(unit.x, unit.y, 24, '#a3e635', 0.25);
          }
        }
      }

      // 2. Spawn 2 Bastion Locks at t >= 2.0s
      if (sig.timer >= 2.0 && !sig.bastionsSpawned) {
        sig.bastionsSpawned = true;
        const bastionCard = getCardByName('Bastion Lock');
        if (bastionCard) {
          const spawnBase = this.bases?.[sig.team];
          const spawnX = spawnBase ? (spawnBase.x + sig.dir * 60) : (sig.originX + sig.dir * 60);
          const laneCenterY = this.laneY + (this.laneHeight / 2);

          this.spawnRaw(bastionCard, sig.team, spawnX, laneCenterY - 32, {
            isBastionRearGuard: true,
            isPriorityTarget: true
          });
          this.spawnRaw(bastionCard, sig.team, spawnX, laneCenterY + 32, {
            isBastionRearGuard: true,
            isPriorityTarget: true
          });

          if (particles?.addShockwave) {
            particles.addShockwave(spawnX, laneCenterY, 48, '#00f0ff', 0.35);
          }
        }
      }

      // 3. Remove signal when duration elapsed
      if (sig.timer >= sig.duration) {
        this.activeRetreatSignals.splice(i, 1);
      }
    }
  }

  handleUnitRetreat(unit) {
    if (!unit || unit.isDead) return;
    const team = unit.team;
    const refund = unit.cardData?.cost || 0;

    if (refund > 0 && this.economy) {
      const res = this.economy.resources?.[team];
      if (res) {
        const currentPlasma = res.plasma || 0;
        const maxCap = 150;
        const newPlasma = Math.min(maxCap, currentPlasma + refund);
        const actualGained = Math.max(0, newPlasma - currentPlasma);
        res.plasma = newPlasma;
        if (newPlasma >= maxCap) {
          this.stats?.recordPlasmaCapped?.(team);
        }
        if (actualGained > 0) {
          this.stats?.recordPlasmaHarvested?.(team, actualGained);
        }
      }
    }

    const p = this.particles || (typeof window !== 'undefined' ? window.tcgGame?.particles : null);
    if (p) {
      p.addDamageText?.(unit.x, unit.y - (unit.radius || 20) - 8, `+${refund} PLASMA`, 'heal');
      p.addShockwave?.(unit.x, unit.y, 28, '#00ff88', 0.3);
      p.addVaporPuff?.(unit.x, unit.y, 14, 0.4);
    }

    const a = this.audio || (typeof window !== 'undefined' ? window.tcgGame?.audio : null);
    a?.playSample?.('even more sounds/80-CC0-creature-SFX/alien_01.ogg', { volume: 0.6, x: unit.x });

    unit.isDead = true;
    unit.vanishedAtBase = true;
  }
}
